import { Injectable } from '@angular/core';
import {
    AngularFirestore,
    QueryDocumentSnapshot,
} from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import {
    Observable,
    BehaviorSubject,
    of,
    forkJoin,
    Subject,
    combineLatest,
    throwError,
    lastValueFrom,
    firstValueFrom,
} from 'rxjs';
import {
    map,
    switchMap,
    take,
    filter,
    tap,
    catchError,
    timeout,
} from 'rxjs/operators';
import {
    ChatUser,
    FirestoreUser,
    UserProfile,
    Chat,
    ChatMessage,
    NewChat,
    LegacyConversation,
    NewMessage,
    UserChat,
    ChatWithUser,
    ChatSummary,
} from '../model/chat.model';
import { LookupService } from './lookup.service';
import { HttpResponse } from '@angular/common/http';
import { WebStorage } from '../components/auth/storage/web.storage';
import * as moment from 'moment-timezone';
import { Common } from '../utils/common';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { from } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ChatService {
    private readonly _selectedChatSubject =
        new BehaviorSubject<ChatWithUser | null>(null);
    public readonly selectedChat$ = this._selectedChatSubject.asObservable();

    private readonly _newMessageSubject = new Subject<
        ChatMessage & { chatId: string }
    >();
    public readonly newMessage$ = this._newMessageSubject.asObservable();

    private readonly _chatUpdatedSubject = new Subject<{
        chatId: string;
        lastMessage: any;
        unreadCount?: number;
    }>();
    public readonly chatUpdated$ = this._chatUpdatedSubject.asObservable();

    private currentUserId: string = '';

    private sidebarRefreshSource = new Subject<void>();
    sidebarRefresh$ = this.sidebarRefreshSource.asObservable();

    // Global real-time listeners for all user chats
    private globalChatListeners: { [chatId: string]: () => void } = {};

    // Cache for search results (searchTerm -> results with timestamp)
    private searchCache = new Map<
        string,
        { results: any[]; timestamp: number }
    >();
    private readonly SEARCH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    constructor(
        private firestore: AngularFirestore,
        private storage: AngularFireStorage,
        private lookupService: LookupService,
        private webStorage: WebStorage,
    ) {
        this.currentUserId = this.webStorage.getUserData().refTableId; // Replace with actual user ID
    }

    triggerSidebarRefresh() {
        this.sidebarRefreshSource.next();
    }

    emitChatUpdate(chatId: string, lastMessage: any, unreadCount?: number) {
        this._chatUpdatedSubject.next({
            chatId,
            lastMessage,
            unreadCount,
        });
    }

    // Create or update a user in Firestore
    // Update chats to include schoolId for a user
    private async updateUserChatsSchoolId(
        userId: string,
        schoolId: number,
    ): Promise<void> {
        try {
            // Get all chats where this user is a participant
            const chatsSnapshot = await this.firestore
                .collection<Chat>('chats', (ref) =>
                    ref.where(`participants.${userId}`, '==', true),
                )
                .get()
                .toPromise();

            if (!chatsSnapshot) return;

            const batch = this.firestore.firestore.batch();
            const chatsRef = this.firestore.collection<Chat>('chats');
            let hasUpdates = false;

            // Update each chat to include schoolId if not set
            for (const doc of chatsSnapshot.docs) {
                const chatData = doc.data() as Chat;
                if (!('schoolId' in chatData) || !chatData.schoolId) {
                    const chatRef = chatsRef.doc(doc.id).ref;
                    batch.update(chatRef, {
                        schoolId,
                        'info.updatedAt': Date.now(),
                    });
                    hasUpdates = true;
                }
            }

            // Commit batch if there are updates
            if (hasUpdates) {
                await batch.commit();
            }

            // Also update userChats collection
            const userChatsRef = this.firestore
                .collection('userChats')
                .doc(userId);
            const userChatsDoc = await userChatsRef
                .collection('chats')
                .get()
                .toPromise();

            if (userChatsDoc && !userChatsDoc.empty) {
                const userChatsBatch = this.firestore.firestore.batch();
                let hasUserChatUpdates = false;

                userChatsDoc.docs.forEach((doc) => {
                    const chatData = doc.data() as { schoolId?: number };
                    if (!chatData.schoolId) {
                        const chatRef = userChatsRef
                            .collection('chats')
                            .doc(doc.id).ref;
                        userChatsBatch.update(chatRef, {
                            schoolId,
                            updatedAt: Date.now(),
                        });
                        hasUserChatUpdates = true;
                    }
                });

                if (hasUserChatUpdates) {
                    await userChatsBatch.commit();
                }
            }
        } catch (error) {
            console.error('Error updating user chats with school ID:', error);
            throw error;
        }
    }

    async createOrUpdateUser(userData: {
        id: string | number;
        name: string;
        email: string;
        role: 'staff' | 'student' | 'parent';
        photoUrl?: string;
        schoolId: number;
        roomId?: number;
        roomIds?: number[];
        phoneNumber?: string;
        status?: string;
        profileStatus?: string;
        employeeId?: string;
        studentUniqueId?: string;
        relation?: string;
    }): Promise<void> {
        if (!userData.schoolId) {
            throw new Error('School ID is required');
        }

        if (!userData.id) {
            throw new Error('User ID is required');
        }

        if (!userData.name) {
            throw new Error('User name is required');
        }

        // Format the user ID based on role
        const formattedId =
            userData.role === 'staff'
                ? `staff_${userData.id}`
                : userData.role === 'student'
                  ? `student_${userData.id}`
                  : `parent_${userData.id}`;

        // Define user profile with proper typing
        interface UserProfileWithExtras extends UserProfile {
            roomIds?: number[];
            roomId?: number;
            schoolId: number;
        }

        const userProfile: UserProfileWithExtras = {
            name: userData.name.trim(),
            email: userData.email || `${formattedId}@example.com`,
            photoURL: userData.photoUrl || 'assets/layout/images/male.png',
            lastSeen: Date.now(),
            // Store real profile status (A, I, Upcoming) instead of hardcoded 'online'
            status: userData.profileStatus || userData.status || 'A',
            role: userData.role,
            schoolId: userData.schoolId,
        };

        // Add room information based on role
        const userDataToStore = {
            id: formattedId,
            profile: userProfile,
            schoolId: userData.schoolId,
            role: userData.role,
            roomId: userData.roomId || 0,
            roomIds: userData.roomIds || [],
            updatedAt: Date.now(),
        };

        // For staff, store roomIds array
        if (userData.role === 'staff') {
            const roomIds = userData.roomIds || [];
            userDataToStore.roomIds = roomIds;
            userProfile.roomIds = roomIds;
        } else {
            // For students and parents, store single roomId
            const roomId = userData.roomId || 0;
            userDataToStore.roomId = roomId;
            userProfile.roomId = roomId;
        }

        try {
            const userRef = this.firestore.doc(`users/${formattedId}`);

            // First get the existing document to check if we need to preserve any fields
            const doc = await userRef.get().toPromise();
            const existingData = doc?.exists ? (doc.data() as any) : null;

            // If user exists and has schoolId, ensure we don't change it to a different school
            if (
                existingData?.schoolId &&
                existingData.schoolId !== userData.schoolId
            ) {
                throw new Error('Cannot change user school once set');
            }

            // Merge with existing data to preserve any additional fields
            const finalData = {
                ...(existingData || {}),
                ...userDataToStore,
                profile: {
                    ...((existingData?.profile as object) || {}),
                    ...userProfile,
                },
                updatedAt: Date.now(),
                createdAt: existingData?.createdAt || Date.now(),
            };

            await userRef.set(finalData, { merge: true });

            // Update the user's chats to include the schoolId if not set
            await this.updateUserChatsSchoolId(formattedId, userData.schoolId);
        } catch (error) {
            console.error('Error creating/updating user:', error);
            throw error;
        }
    }

    // Check if a chat already exists for a student
    private async findExistingChat(
        studentId: string,
        staffId: string,
    ): Promise<string | null> {
        try {
            const currentUser = this.webStorage.getUserData();
            if (!currentUser?.schoolId) {
                throw new Error('User school ID not found');
            }

            // First check in chats collection for existing chat with this staff and student
            const chatQuerySnapshot = await this.firestore
                .collection<Chat>('chats', (ref) => {
                    let query = ref
                        .where('studentInfo.id', '==', `student_${studentId}`)
                        .where(`participants.staff_${staffId}`, '==', true);

                    // Add school filter if user is not admin
                    query = query.where('schoolId', '==', currentUser.schoolId);

                    return query;
                })
                .get()
                .toPromise();

            if (chatQuerySnapshot && !chatQuerySnapshot.empty) {
                const chatId = chatQuerySnapshot.docs[0].id;

                // Verify the chat exists in userChats for the staff member
                const userChatDoc = await this.firestore
                    .collection('userChats')
                    .doc(`staff_${staffId}`)
                    .collection('chats')
                    .doc(chatId)
                    .get()
                    .toPromise();

                if (userChatDoc?.exists) {
                    return chatId;
                }
            }

            // If not found, check for any existing chat with this student and roomId
            const studentChatQuerySnapshot = await this.firestore
                .collection<Chat>('chats', (ref) => {
                    let query = ref.where(
                        'studentInfo.id',
                        '==',
                        `student_${studentId}`,
                    );

                    // Add school filter if user is not admin
                    query = query.where('schoolId', '==', currentUser.schoolId);

                    return query;
                })
                .get()
                .toPromise();

            if (studentChatQuerySnapshot && !studentChatQuerySnapshot.empty) {
                const chatDoc = studentChatQuerySnapshot.docs[0];
                const chatData = chatDoc.data();

                // If chat exists, add the new staff member to participants
                if (chatData) {
                    const chatId = chatDoc.id;
                    const participants = chatData.participants || {};
                    participants[`staff_${staffId}`] = true;

                    // Update the chat with new participant
                    await this.firestore
                        .collection('chats')
                        .doc(chatId)
                        .update({
                            participants: participants,
                        });

                    // Add chat to staff's userChats
                    await this.firestore
                        .collection('userChats')
                        .doc(`staff_${staffId}`)
                        .collection('chats')
                        .doc(chatId)
                        .set({
                            ...chatData,
                            participants: participants,
                        });

                    return chatId;
                }
            }

            return null;
        } catch (error) {
            console.error('Error finding existing chat:', error);
            return null;
        }
    }

    public async findExistingChatByStudentId(
        studentId: string,
    ): Promise<string | null> {
        try {
            const currentUser = this.webStorage.getUserData();
            if (!currentUser?.schoolId) {
                throw new Error('User school ID not found');
            }

            const chatQuerySnapshot = await this.firestore
                .collection<Chat>('chats', (ref) =>
                    ref
                        .where('studentInfo.id', '==', `student_${studentId}`)
                        .where('schoolId', '==', currentUser.schoolId),
                )
                .get()
                .toPromise();

            if (chatQuerySnapshot && !chatQuerySnapshot.empty) {
                return chatQuerySnapshot.docs[0].id;
            }

            return null;
        } catch (error) {
            console.error('Error finding existing chat:', error);
            return null;
        }
    }

    // Create a new chat with initial message
    async createNewChat(newChat: NewChat): Promise<string> {
        // First check if a chat already exists
        const existingChatId = await this.findExistingChat(
            newChat.studentId,
            newChat.staffId,
        );
        if (existingChatId) {
            // Send message to existing chat
            const message: ChatMessage = {
                id: Date.now().toString(),
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                timestamp: Date.now(),
                type: 'text',
                status: 'sent',
                isPrivate: false,
                targetGuardianId: '',
                isDeleted: false,
            };

            await lastValueFrom(this.sendMessage(existingChatId, message));
            return existingChatId;
        }

        // If no existing chat, create a new one
        const chatId = `chat_${Date.now()}`;
        const messageId = Date.now().toString();
        const timestamp = Date.now();

        // Create participants object with all staff and guardians
        const participants: { [key: string]: boolean } = {
            [`staff_${newChat.staffId}`]: true,
        };

        // Add all staff from staffList
        if (newChat.staffList) {
            newChat.staffList.forEach((staffIds) => {
                participants[`staff_${staffIds}`] = true;
            });
        }

        // Add all guardians
        if (newChat.guardianIds) {
            newChat.guardianIds.forEach((guardianId) => {
                participants[`parent_${guardianId}`] = true;
            });
        }

        // Create initial message
        const initialMessage: ChatMessage = {
            id: messageId,
            text: newChat.initialMessage,
            senderId: `staff_${newChat.staffId}`,
            timestamp: timestamp,
            isDeleted: false,
            type: 'text',
            status: 'sent',
            isPrivate: false,
            readBy: {
                [`staff_${newChat.staffId}`]: true,
            },
        };

        // Create chat document
        const chat: Chat = {
            chatId: chatId,
            info: {
                type: 'group',
                createdAt: timestamp,
                updatedAt: timestamp,
                adminOnly: false,
                isPrivate: false,
                roomId: newChat.roomId || 0, // Provide default value if roomId is undefined
            },
            studentInfo: {
                id: `student_${newChat.studentId}`,
                name: newChat.studentName,
                roomId: newChat.roomId || 0, // Provide default value if roomId is undefined
            },
            participants: participants,
            messages: [initialMessage],
            cursor: {
                [`staff_${newChat.staffId}`]: messageId,
                ...Object.keys(participants).reduce(
                    (acc, id) => ({ ...acc, [id]: messageId }),
                    {},
                ),
            },
            createdAt: timestamp,
            lastMessage: {
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                timestamp: timestamp,
                isDeleted: false,
            },
            staffInfo: {
                id: `staff_${newChat.staffId}`,
                name: newChat.staffName,
                roomIds: newChat.roomId ? [newChat.roomId] : [], // Create array with roomId if it exists
            },
            schoolId: this.webStorage.getUserData()?.schoolId,
        };

        // Batch write
        const batch = this.firestore.firestore.batch();

        // Set chat document
        const chatRef = this.firestore.doc(`chats/${chatId}`).ref;
        batch.set(chatRef, chat);

        // Update userChats for all participants
        const userChatData = {
            lastMessage: {
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                timestamp: timestamp,
                isDeleted: false,
            },
            unreadCount: 0,
            studentInfo: chat.studentInfo,
            staffInfo: chat.staffInfo,
        };

        // Add userChats for all participants
        Object.keys(participants).forEach((participantId) => {
            const participantChatRef = this.firestore
                .collection('userChats')
                .doc(participantId)
                .collection('chats')
                .doc(chatId).ref;

            // Set lastVisibleMessage based on participant type
            const lastVisibleMessage = {
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                timestamp: timestamp,
                isDeleted: false,
            };

            batch.set(participantChatRef, {
                ...userChatData,
                lastVisibleMessage,
                unreadCount:
                    participantId !== `staff_${newChat.staffId}` ? 1 : 0,
            });
        });

        try {
            await batch.commit();
            return chatId;
        } catch (error) {
            console.error('Error creating chat:', error);
            throw error;
        }
    }

    // Set selected chat
    setSelectedChat(chat: ChatWithUser | null) {
        this._selectedChatSubject.next(chat);
    }

    // Get selected chat
    getSelectedChat(): Observable<ChatWithUser | null> {
        return this.selectedChat$;
    }

    // Get messages for selected chat, optionally filtered by private mode
    getSelectedChatMessages(privateUserId?: string): Observable<ChatMessage[]> {
        return this.selectedChat$.pipe(
            filter((chat) => !!chat),
            switchMap((chat) => {
                return this.firestore
                    .doc<Chat>(`chats/${chat!.chatId}`)
                    .valueChanges()
                    .pipe(
                        map((chatDoc) => {
                            if (!chatDoc || !chatDoc.messages) {
                                return [];
                            }
                            let messages = chatDoc.messages.sort(
                                (a, b) => a.timestamp - b.timestamp,
                            );
                            if (privateUserId) {
                                messages = messages.filter(
                                    (msg) =>
                                        msg.isPrivate &&
                                        msg.senderId === privateUserId,
                                );
                            }
                            return messages;
                        }),
                    );
            }),
        );
    }

    getUserChats(userId: string): Observable<ChatWithUser[]> {
        const currentUser = this.webStorage.getUserData();

        return this.firestore
            .collection<Chat>('chats', (ref) => {
                return ref
                    .where(`participants.${userId}`, '==', true)
                    .where('schoolId', '==', currentUser.schoolId);
                // let query = ref.where(`participants.${userId}`, '==', true);

                // // Add school filter if user is not admin and has schoolId
                //   query = query.where('schoolId', '==', currentUser.schoolId) as any;

                // return query;
            })
            .snapshotChanges()
            .pipe(
                map((actions) =>
                    actions.map((a) => {
                        const data = a.payload.doc.data() as Chat;
                        const chatId = a.payload.doc.id;
                        return { ...data, chatId };
                    }),
                ),
                switchMap((chats) => {
                    if (!chats || chats.length === 0) {
                        return of([]);
                    }

                    const userChatObservables = chats.map((chat) => {
                        const chatId = chat.chatId;

                        const userChatRef = this.firestore
                            .collection('userChats')
                            .doc(userId)
                            .collection<UserChat>('chats')
                            .doc(chatId)
                            .valueChanges()
                            .pipe(
                                take(1),
                                switchMap((userChat) => {
                                    if (userChat) {
                                        return of({
                                            chatId,
                                            data: userChat,
                                            participants: chat.participants,
                                            roomId: chat.info?.roomId || 0,
                                        });
                                    } else {
                                        // Skip chats that don't have userChat entries - they may have been manually deleted
                                        // Only create userChat entries during actual chat creation, not during queries
                                        console.warn(
                                            `Skipping chat ${chatId} - missing userChat entry (may have been manually deleted)`,
                                        );
                                        return of(null); // Skip this chat
                                    }
                                }),
                            );

                        return userChatRef;
                    });

                    return forkJoin(userChatObservables).pipe(
                        switchMap((userChatArray: any) => {
                            // Filter out null values (old chats that were skipped)
                            const validUserChats = userChatArray.filter(
                                (chat: any) => chat !== null,
                            );

                            const chats: ChatWithUser[] = [];
                            // Sort the array by lastMessage.timestamp in descending order
                            validUserChats.sort((a: any, b: any) => {
                                const timestampA =
                                    a.data.lastMessage?.timestamp || 0;
                                const timestampB =
                                    b.data.lastMessage?.timestamp || 0;
                                return timestampB - timestampA;
                            });

                            const participantDetailsObservables =
                                validUserChats.map(
                                    (chatData: {
                                        chatId: string;
                                        data: UserChat;
                                        participants: any;
                                        roomId: number;
                                    }) => {
                                        const {
                                            chatId,
                                            data,
                                            participants,
                                            roomId,
                                        } = chatData;
                                        const isStaff =
                                            userId.startsWith('staff_');
                                        // const otherUser = isStaff ? data.studentInfo : data.staffInfo;
                                        const otherUser = data.studentInfo;

                                        if (otherUser) {
                                            // Get staff and parent IDs from participants
                                            const staffIds = Object.keys(
                                                participants,
                                            )
                                                .filter((id) =>
                                                    id.startsWith('staff_'),
                                                )
                                                .map((id) =>
                                                    id.replace('staff_', ''),
                                                );

                                            const parentIds = Object.keys(
                                                participants,
                                            )
                                                .filter((id) =>
                                                    id.startsWith('parent_'),
                                                )
                                                .map((id) =>
                                                    id.replace('parent_', ''),
                                                );

                                            // Fetch staff and parent details
                                            const staffObservables =
                                                staffIds.map((id) =>
                                                    this.firestore
                                                        .doc<{
                                                            id: string;
                                                            profile: {
                                                                name: string;
                                                                email: string;
                                                                photoURL: string;
                                                                status: string;
                                                                roomIds?: number[];
                                                            };
                                                        }>(`users/staff_${id}`)
                                                        .valueChanges()
                                                        .pipe(
                                                            take(1),
                                                            map((staff) => ({
                                                                id: parseInt(
                                                                    id,
                                                                ),
                                                                name:
                                                                    staff
                                                                        ?.profile
                                                                        ?.name ||
                                                                    '',
                                                                email:
                                                                    staff
                                                                        ?.profile
                                                                        ?.email ||
                                                                    '',
                                                                role: 'staff',
                                                                photoUrl:
                                                                    staff
                                                                        ?.profile
                                                                        ?.photoURL ||
                                                                    'assets/layout/images/male.png',
                                                                createdAt:
                                                                    Date.now(),
                                                                status:
                                                                    staff
                                                                        ?.profile
                                                                        ?.status ||
                                                                    'offline',
                                                                roomIds:
                                                                    staff
                                                                        ?.profile
                                                                        ?.roomIds ||
                                                                    [],
                                                            })),
                                                        ),
                                                );

                                            const parentObservables =
                                                parentIds.map((id) =>
                                                    this.firestore
                                                        .doc<{
                                                            id: string;
                                                            profile: {
                                                                name: string;
                                                                email: string;
                                                                photoURL: string;
                                                                status: string;
                                                                roomId?: number;
                                                            };
                                                        }>(`users/parent_${id}`)
                                                        .valueChanges()
                                                        .pipe(
                                                            take(1),
                                                            map((parent) => ({
                                                                id: parseInt(
                                                                    id,
                                                                ),
                                                                name:
                                                                    parent
                                                                        ?.profile
                                                                        ?.name ||
                                                                    '',
                                                                email:
                                                                    parent
                                                                        ?.profile
                                                                        ?.email ||
                                                                    '',
                                                                role: 'parent',
                                                                photoUrl:
                                                                    parent
                                                                        ?.profile
                                                                        ?.photoURL ||
                                                                    'assets/layout/images/male.png',
                                                                createdAt:
                                                                    Date.now(),
                                                                status:
                                                                    parent
                                                                        ?.profile
                                                                        ?.status ||
                                                                    'offline',
                                                                roomId: parent
                                                                    ?.profile
                                                                    ?.roomId,
                                                            })),
                                                        ),
                                                );

                                            return forkJoin([
                                                forkJoin(staffObservables),
                                                forkJoin(parentObservables),
                                            ]).pipe(
                                                switchMap(
                                                    ([
                                                        staffDetails,
                                                        parentDetails,
                                                    ]) => {
                                                        const otherUserId =
                                                            otherUser.id;

                                                        const targetPath =
                                                            isStaff
                                                                ? `users/${otherUserId}`
                                                                : `users/${otherUserId}`;

                                                        return this.firestore
                                                            .doc<{
                                                                profile: {
                                                                    photoURL: string;
                                                                };
                                                            }>(targetPath)
                                                            .valueChanges()
                                                            .pipe(
                                                                take(1),
                                                                map(
                                                                    (
                                                                        photoDoc,
                                                                    ) => {
                                                                        const photoUrl =
                                                                            photoDoc
                                                                                ?.profile
                                                                                ?.photoURL ||
                                                                            'assets/layout/images/male.png';

                                                                        const chatWithUser: ChatWithUser =
                                                                            {
                                                                                chatId,
                                                                                lastMessage:
                                                                                    data.lastMessage,
                                                                                lastVisibleMessage:
                                                                                    data.lastVisibleMessage,
                                                                                unreadCount:
                                                                                    data.unreadCount,
                                                                                otherUser:
                                                                                    {
                                                                                        id: otherUser.id,
                                                                                        name: otherUser.name,
                                                                                        email: '',
                                                                                        role: isStaff
                                                                                            ? 'student'
                                                                                            : 'staff',
                                                                                        photoUrl,
                                                                                        createdAt:
                                                                                            Date.now(),
                                                                                        status: 'online',
                                                                                        guardians:
                                                                                            parentDetails ||
                                                                                            [],
                                                                                        staffDTOList:
                                                                                            staffDetails ||
                                                                                            [],
                                                                                        roomId,
                                                                                        roomIds:
                                                                                            isStaff
                                                                                                ? staffDetails[0]
                                                                                                      ?.roomIds
                                                                                                : undefined,
                                                                                    },
                                                                            };

                                                                        return chatWithUser;
                                                                    },
                                                                ),
                                                            );
                                                    },
                                                ),
                                            );
                                        }
                                        return of(null);
                                    },
                                );

                            return forkJoin<(ChatWithUser | null)[]>(
                                participantDetailsObservables,
                            ).pipe(
                                map((results: (ChatWithUser | null)[]) =>
                                    results.filter(
                                        (
                                            chat: ChatWithUser | null,
                                        ): chat is ChatWithUser =>
                                            chat !== null,
                                    ),
                                ),
                            );
                        }),
                    );
                }),
            );
    }

    /**
     * Get lightweight chat summaries for sidebar display - optimized for performance
     * Uses userChats collection directly to avoid heavy ChatWithUser processing
     * @param userId The user ID
     * @returns Observable of ChatSummary array
     */
    getUserChatSummaries(userId: string): Observable<ChatSummary[]> {
        // Use query without schoolId filter since it's not stored in userChats collection
        return this.firestore
            .collection('userChats')
            .doc(userId)
            .collection<UserChat>('chats')
            .valueChanges({ idField: 'chatId' })
            .pipe(
                map((userChats: (UserChat & { chatId: string })[]) => {
                    // Sort in memory by lastMessage.timestamp (newest first)
                    const sortedChats = userChats.sort((a, b) => {
                        const timestampA = a.lastMessage?.timestamp || 0;
                        const timestampB = b.lastMessage?.timestamp || 0;
                        return timestampB - timestampA;
                    });

                    return sortedChats.map((userChat) => {
                        const chatSummary: ChatSummary = {
                            chatId: userChat.chatId,
                            lastMessage: userChat.lastMessage,
                            lastVisibleMessage: userChat.lastVisibleMessage,
                            unreadCount: userChat.unreadCount,
                            studentInfo: userChat.studentInfo,
                            staffInfo: userChat.staffInfo,
                            otherUser: {
                                id: userChat.studentInfo?.id || '',
                                name:
                                    userChat.studentInfo?.name ||
                                    'Unknown User',
                                role: userId.startsWith('staff_')
                                    ? 'student'
                                    : 'staff',
                                photoUrl: 'assets/layout/images/male.png', // Will be resolved by PhotoService
                                roomId: 0, // Will be populated later if needed
                                roomIds: [], // Will be populated later if needed
                            },
                            isPrivateMode: userChat.isPrivateMode,
                        };
                        return chatSummary;
                    });
                }),
                switchMap((chatSummaries: ChatSummary[]) => {
                    if (chatSummaries.length === 0) {
                        return of(chatSummaries);
                    }

                    // 🔹 Validate that chats still exist in main collection to filter out orphaned entries
                    const chatIds = chatSummaries.map((s) => s.chatId);
                    const validationPromises = chatIds.map((chatId) =>
                        this.firestore
                            .doc(`chats/${chatId}`)
                            .get()
                            .toPromise()
                            .then((doc) => ({
                                chatId,
                                exists: doc?.exists || false,
                            }))
                            .catch(() => ({ chatId, exists: false })),
                    );

                    return from(Promise.all(validationPromises)).pipe(
                        switchMap((validationResults) => {
                            // Filter out summaries for chats that no longer exist
                            const validSummaries = chatSummaries.filter(
                                (summary) =>
                                    validationResults.find(
                                        (v) => v.chatId === summary.chatId,
                                    )?.exists,
                            );

                            if (validSummaries.length === 0) {
                                return of([]);
                            }

                            // Fetch photo URLs for valid chat summaries in parallel
                            const photoObservables = validSummaries.map(
                                (chatSummary) => {
                                    const studentId = chatSummary.otherUser.id;
                                    if (!studentId || studentId === '') {
                                        return of(
                                            'assets/layout/images/male.png',
                                        );
                                    }

                                    return this.firestore
                                        .doc(`users/${studentId}`)
                                        .valueChanges()
                                        .pipe(
                                            take(1),
                                            map((user: any) => {
                                                const photoUrl =
                                                    user?.profile?.photoURL;
                                                return (
                                                    photoUrl ||
                                                    'assets/layout/images/male.png'
                                                );
                                            }),
                                            catchError((error) => {
                                                console.error(
                                                    `❌ [ChatService] Error loading photo for ${studentId}:`,
                                                    error,
                                                );
                                                return of(
                                                    'assets/layout/images/male.png',
                                                );
                                            }),
                                        );
                                },
                            );

                            return forkJoin(photoObservables).pipe(
                                map((photoUrls: string[]) => {
                                    // Update each chat summary with its photo URL
                                    return validSummaries.map(
                                        (summary, index) => ({
                                            ...summary,
                                            otherUser: {
                                                ...summary.otherUser,
                                                photoUrl: photoUrls[index],
                                            },
                                        }),
                                    );
                                }),
                                catchError((error) => {
                                    console.error(
                                        'Error fetching photo URLs for chat summaries:',
                                        error,
                                    );
                                    // Return summaries with default photo URLs on error
                                    return of(validSummaries);
                                }),
                            );
                        }),
                    );
                }),
                catchError((error) => {
                    console.error('Error getting user chat summaries:', error);
                    return of([]);
                }),
            );
    }

    /**
     * Get user photo URL - centralized method for photo fetching
     * @param userId The user ID (e.g., 'student_123', 'staff_456', 'parent_789')
     * @returns Observable of photo URL string
     */
    getUserPhotoUrl(userId: string): Observable<string> {
        if (!userId) {
            return of('assets/layout/images/male.png');
        }

        return this.firestore
            .doc(`users/${userId}`)
            .valueChanges()
            .pipe(
                take(1),
                map(
                    (user: any) =>
                        user?.profile?.photoURL ||
                        'assets/layout/images/male.png',
                ),
                catchError(() => of('assets/layout/images/male.png')),
            );
    }

    // Get all messages for a chat with school validation
    getChatMessages(chatId: string, limit = 50): Observable<ChatMessage[]> {
        return this.firestore
            .doc<Chat>(`chats/${chatId}`)
            .valueChanges()
            .pipe(
                map((chat) => {
                    if (!chat || !chat.messages) {
                        return [];
                    }
                    // Sort messages by timestamp and apply limit
                    const sortedMessages = chat.messages
                        .sort((a, b) => a.timestamp - b.timestamp)
                        .slice(-limit); // Get last 'limit' messages (most recent)
                    return sortedMessages;
                }),
                catchError((error) => {
                    console.error('Error getting chat messages:', error);
                    return of([]);
                }),
            );
    }

    // Get paginated messages for a chat (loads all but returns in chunks)
    getChatMessagesPaginated(
        chatId: string,
        pageSize: number = 10,
        lastDoc?: QueryDocumentSnapshot<ChatMessage>,
    ): Observable<{
        messages: ChatMessage[];
        lastDoc?: QueryDocumentSnapshot<ChatMessage>;
        hasMore: boolean;
    }> {
        return this.firestore
            .doc<Chat>(`chats/${chatId}`)
            .valueChanges()
            .pipe(
                take(1),
                map((chat) => {
                    if (!chat || !chat.messages) {
                        return { messages: [], hasMore: false };
                    }

                    // Sort messages by timestamp (oldest first)
                    const sortedMessages = chat.messages.sort(
                        (a, b) => a.timestamp - b.timestamp,
                    );

                    // For pagination, we need to simulate pagination from the array
                    // Since we have all messages, we'll slice them based on pageSize
                    const startIndex = lastDoc
                        ? this.findMessageIndex(sortedMessages, lastDoc.id) + 1
                        : 0;
                    const endIndex = startIndex + pageSize;
                    const messages = sortedMessages.slice(startIndex, endIndex);

                    return {
                        messages,
                        lastDoc: messages.length
                            ? ({ id: messages[messages.length - 1].id } as any)
                            : undefined,
                        hasMore: endIndex < sortedMessages.length,
                    };
                }),
                catchError((error) => {
                    console.error('Paginated messages error:', error);
                    return of({ messages: [], hasMore: false });
                }),
            );
    }

    private findMessageIndex(
        messages: ChatMessage[],
        messageId: string,
    ): number {
        return messages.findIndex((msg) => msg.id === messageId);
    }

    // Get chat details with school validation
    getChat(chatId: string): Observable<Chat | null> {
        const currentUser = this.webStorage.getUserData();

        return this.firestore
            .doc<Chat>(`chats/${chatId}`)
            .valueChanges()
            .pipe(
                map((chat) => {
                    if (!chat) {
                        return null;
                    }
                    return chat;
                }),
                catchError((error) => {
                    console.error('Error getting chat:', error);
                    return of(null);
                }),
            );
    }

    // Update sendMessage to handle both old and new signatures
    /**
     * Sends a message to a chat
     * Supports multiple function signatures:
     * 1. sendMessage(chatId: string, message: ChatMessage): Observable<void>
     * 2. sendMessage(newMessage: NewMessage): Observable<void>
     */
    sendMessage(
        chatIdOrMessage: string | NewMessage,
        message?: ChatMessage,
    ): Observable<void> {
        // Case 1: Old signature with chatId and message
        if (typeof chatIdOrMessage === 'string' && message) {
            const chatId = chatIdOrMessage;
            return this.getChat(chatId).pipe(
                take(1),
                switchMap((chat) => {
                    if (!chat) {
                        return throwError(() => new Error('Chat not found'));
                    }
                    const updatedMessages = [...(chat.messages || []), message];
                    const participants = Object.keys(chat.participants || {});
                    const batch = this.firestore.firestore.batch();
                    const chatRef = this.firestore
                        .collection('chats')
                        .doc(chatId).ref;
                    batch.update(chatRef, {
                        messages: updatedMessages,
                        lastMessage: {
                            text: message.text,
                            senderId: message.senderId,
                            timestamp: message.timestamp || Date.now(),
                            status: 'sent',
                        },
                        'info.updatedAt': message.timestamp || Date.now(),
                    });
                    const userChatData = {
                        lastMessage: {
                            isDeleted: message.isDeleted,
                            text: message.text,
                            senderId: message.senderId,
                            timestamp: message.timestamp || Date.now(),
                            status: 'sent',
                        },
                        studentInfo: chat.studentInfo,
                        staffInfo: chat.staffInfo,
                        updatedAt: message.timestamp || Date.now(),
                    };
                    for (const participantId of participants) {
                        const participantChatRef = this.firestore
                            .collection('userChats')
                            .doc(participantId)
                            .collection('chats')
                            .doc(chatId).ref;
                        // --- lastVisibleMessage logic ---
                        const lastVisibleMessage = {
                            isDeleted: message.isDeleted,
                            text: message.text,
                            senderId: message.senderId,
                            timestamp: message.timestamp || Date.now(),
                            status: 'sent',
                            isPrivate: message.isPrivate ?? false,
                        };

                        // Helper function to determine if a participant should see the message
                        const shouldSeeMessage = (
                            participantId: string,
                            message: any,
                        ): boolean => {
                            if (!message.isPrivate) return true;
                            if (participantId.startsWith('staff_')) return true;
                            if (participantId === message.senderId) return true;
                            if (
                                message.targetGuardianId &&
                                participantId ===
                                    `parent_${message.targetGuardianId}`
                            )
                                return true;
                            return false;
                        };

                        // Helper function to determine if unread count should be incremented
                        const shouldIncrementUnread = (
                            participantId: string,
                            message: any,
                        ): boolean => {
                            if (participantId === message.senderId)
                                return false;
                            if (!message.isPrivate) return true;
                            if (participantId.startsWith('staff_')) return true;
                            if (
                                message.targetGuardianId &&
                                participantId ===
                                    `parent_${message.targetGuardianId}`
                            )
                                return true;
                            return false;
                        };

                        if (shouldSeeMessage(participantId, message)) {
                            batch.set(
                                participantChatRef,
                                {
                                    ...userChatData,
                                    lastVisibleMessage,
                                    unreadCount: shouldIncrementUnread(
                                        participantId,
                                        message,
                                    )
                                        ? firebase.firestore.FieldValue.increment(
                                              1,
                                          )
                                        : 0,
                                },
                                { merge: true },
                            );
                        } else {
                            batch.set(
                                participantChatRef,
                                {
                                    ...userChatData,
                                    unreadCount: 0, // Don't increment unread for those who can't see
                                },
                                { merge: true },
                            );
                        }
                    }
                    return from(batch.commit()).pipe(
                        tap(() => {
                            this._newMessageSubject.next({
                                ...message,
                                chatId,
                            });
                        }),
                    );
                }),
            );
        }
        // Case 2: Old signature with NewMessage object
        else if (typeof chatIdOrMessage !== 'string') {
            const newMessage = chatIdOrMessage;
            const chatId = newMessage.chatId;
            const messageData: ChatMessage = {
                id: this.firestore.createId(),
                text: newMessage.text,
                senderId: newMessage.senderId,
                timestamp: Date.now(),
                type: 'text',
                status: 'sent',
                readBy: { [newMessage.senderId]: true },
                isPrivate: newMessage.isPrivate,
                targetGuardianId: newMessage.targetGuardianId,
            };
            return from(
                this.firestore.collection('chats').doc(chatId).get(),
            ).pipe(
                switchMap((chatDoc) => {
                    if (!chatDoc.exists) {
                        return throwError(() => new Error('Chat not found'));
                    }
                    const chat = chatDoc.data() as Chat;
                    const participants = Object.keys(chat.participants || {});
                    const batch = this.firestore.firestore.batch();
                    const chatRef = this.firestore
                        .collection('chats')
                        .doc(chatId).ref;
                    batch.update(chatRef, {
                        messages:
                            firebase.firestore.FieldValue.arrayUnion(
                                messageData,
                            ),
                        lastMessage: {
                            isDeleted: messageData.isDeleted,
                            text: messageData.text,
                            senderId: messageData.senderId,
                            timestamp: messageData.timestamp,
                        },
                        'info.updatedAt': messageData.timestamp,
                    });
                    const userChatData = {
                        lastMessage: {
                            isDeleted: messageData.isDeleted,
                            text: messageData.text,
                            senderId: messageData.senderId,
                            timestamp: messageData.timestamp,
                        },
                        studentInfo: chat.studentInfo,
                        staffInfo: chat.staffInfo,
                        updatedAt: messageData.timestamp,
                    };
                    for (const participantId of participants) {
                        const participantChatRef = this.firestore
                            .collection('userChats')
                            .doc(participantId)
                            .collection('chats')
                            .doc(chatId).ref;
                        // --- lastVisibleMessage logic ---
                        const lastVisibleMessage = {
                            isDeleted: messageData.isDeleted,
                            text: messageData.text,
                            senderId: messageData.senderId,
                            timestamp: messageData.timestamp,
                            status: 'sent',
                            isPrivate: messageData.isPrivate ?? false,
                        };

                        // Helper function to determine if a participant should see the message
                        const shouldSeeMessage = (
                            participantId: string,
                            message: any,
                        ): boolean => {
                            if (!message.isPrivate) return true;
                            if (participantId.startsWith('staff_')) return true;
                            if (participantId === message.senderId) return true;
                            if (
                                message.targetGuardianId &&
                                participantId ===
                                    `parent_${message.targetGuardianId}`
                            )
                                return true;
                            return false;
                        };

                        // Helper function to determine if unread count should be incremented
                        const shouldIncrementUnread = (
                            participantId: string,
                            message: any,
                        ): boolean => {
                            if (participantId === message.senderId)
                                return false;
                            if (!message.isPrivate) return true;
                            if (participantId.startsWith('staff_')) return true;
                            if (
                                message.targetGuardianId &&
                                participantId ===
                                    `parent_${message.targetGuardianId}`
                            )
                                return true;
                            return false;
                        };

                        if (shouldSeeMessage(participantId, messageData)) {
                            batch.set(
                                participantChatRef,
                                {
                                    ...userChatData,
                                    lastVisibleMessage,
                                    unreadCount: shouldIncrementUnread(
                                        participantId,
                                        messageData,
                                    )
                                        ? firebase.firestore.FieldValue.increment(
                                              1,
                                          )
                                        : 0,
                                },
                                { merge: true },
                            );
                        } else {
                            batch.set(
                                participantChatRef,
                                {
                                    ...userChatData,
                                    unreadCount: 0, // Don't increment unread for those who can't see
                                },
                                { merge: true },
                            );
                        }
                    }
                    return from(batch.commit()).pipe(
                        tap(() => {
                            this._newMessageSubject.next({
                                ...messageData,
                                chatId,
                            });
                        }),
                    );
                }),
            );
        }
        return throwError(() => new Error('Invalid arguments'));
    }

    // searchMessagesAndStudents(
    //     searchTerm: string,
    //     userId: string,
    //     roomIds?: any[]
    // ): Observable<Array<{ type: 'chat' | 'student'; data: any }>> {
    //     if (!searchTerm.trim()) {
    //         return of([]);
    //     }

    //     const searchTermLower = searchTerm.toLowerCase();
    //     const cacheKey = `${userId}_${searchTermLower}_${JSON.stringify(roomIds || [])}`;

    //     // Check cache first
    //     const cachedResult = this.searchCache.get(cacheKey);
    //     if (cachedResult && (Date.now() - cachedResult.timestamp) < this.SEARCH_CACHE_DURATION) {
    //         return of(cachedResult.results);
    //     }

    //     // 🔹 Fetch chats where the user is a participant (comprehensive search)
    //     const chatSearch$ = this.firestore
    //         .collection<Chat>('chats', (ref) =>
    //             ref.where(`participants.${userId}`, '==', true)
    //         )
    //         .valueChanges()
    //         .pipe(
    //             take(1),
    //             switchMap((chats) => {
    //                 const filteredChats = chats.filter((chat) => {
    //                     const hasMatchingMessage = chat.messages?.some(
    //                         (message) =>
    //                             message.text
    //                                 ?.toLowerCase()
    //                                 .includes(searchTermLower) &&
    //                             !message.isDeleted
    //                     );

    //                     const studentNameMatches = chat.studentInfo?.name
    //                         ?.toLowerCase()
    //                         .includes(searchTermLower);

    //                     return hasMatchingMessage || studentNameMatches;
    //                 });

    //                 if (filteredChats.length === 0) {
    //                     return of([]);
    //                 }

    //                 // Get profile images and lastVisibleMessage for each chat participant
    //                 const chatWithProfileImages$ = filteredChats.map((chat) => {
    //                     const chatId = chat.chatId;
    //                     const participantId = chat.studentInfo?.id;

    //                     if (!participantId) {
    //                         return of({
    //                             type: 'chat' as const,
    //                             data: {
    //                                 ...chat,
    //                                 participantProfileImage: 'assets/layout/images/male.png',
    //                             },
    //                             studentId: chat.studentInfo?.id,
    //                         });
    //                     }

    //                     // Get lastVisibleMessage from userChats
    //                     const userChatRef = this.firestore
    //                         .collection('userChats')
    //                         .doc(userId)
    //                         .collection('chats')
    //                         .doc(chatId);

    //                     return combineLatest([
    //                         this.firestore.doc(`users/${participantId}`).valueChanges().pipe(take(1)),
    //                         userChatRef.valueChanges().pipe(take(1))
    //                     ]).pipe(
    //                         map(([userProfile, userChat]: [any, any]) => {
    //                             const photoUid = userProfile?.profile?.photoURL;
    //                             const lastVisibleMessage = userChat?.lastVisibleMessage || chat.lastMessage;

    //                             return {
    //                                 type: 'chat' as const,
    //                                 data: {
    //                                     ...chat,
    //                                     lastVisibleMessage,
    //                                     participantProfileImage: photoUid || 'assets/layout/images/male.png',
    //                                 },
    //                                 studentId: chat.studentInfo?.id,
    //                             };
    //                         }),
    //                         catchError(() =>
    //                             of({
    //                                 type: 'chat' as const,
    //                                 data: {
    //                                     ...chat,
    //                                     participantProfileImage: 'assets/layout/images/male.png',
    //                                 },
    //                                 studentId: chat.studentInfo?.id,
    //                             })
    //                         )
    //                     );
    //                 });

    //                 return forkJoin(chatWithProfileImages$);
    //             }),
    //             catchError((error) => {
    //                 console.error('❌ Service: Error fetching chats:', error);
    //                 return of([]);
    //             })
    //         );
    //     const currentUser = this.webStorage.getUserData();
    //     // 🔹 Fetch students if the user is a staff member
    //     const studentSearch$ = userId.startsWith('staff_')
    //         ? (() => {
    //               // Build query parameters for comprehensive search
    //               const queryParams: any = {
    //                   'fullName.contains': searchTerm,
    //                   'schoolId.equals': currentUser.schoolId,
    //                   'sort': 'fullName,asc'
    //                   // Removed size limit to allow comprehensive search
    //               };

    //               // Add room filtering for staff users (not admins)
    //               if (roomIds && roomIds.length > 0 && !Common.isAdminUserLogin(this.webStorage)) {
    //                   queryParams['roomId.in'] = roomIds;
    //               }

    //               return this.lookupService
    //                   .queryChatStudent(queryParams)
    //                   .pipe(
    //                       map((response: HttpResponse<any[]>) => {
    //                           const students = response.body || [];

    //                           return students.map((student) => {
    //                               const studentId = `student_${student.id}`;
    //                               const photoUuid = student.photoUuid || student.photoURL;

    //                               return {
    //                                   type: 'student' as const,
    //                                   data: {
    //                                       id: studentId,
    //                                       profile: {
    //                                           name: student.name || student.fullName || '',
    //                                           photoURL: photoUuid || 'assets/layout/images/male.png',
    //                                           photoUid: photoUuid, // Keep the original photoUid for later processing
    //                                       },
    //                                       guardians: [...student.guardian],
    //                                       staffDTOList: [...student.staffDTOList],
    //                                       roomId: student.roomId,
    //                                   },
    //                                   studentId: studentId,
    //                               };
    //                           });
    //                       }),
    //                       catchError((error) => {
    //                           console.error('❌ Service: Error fetching students:', error);
    //                           return of([]);
    //                       })
    //                   );
    //           })()
    //         : of([]);

    //     // 🔹 Combine both searches and remove duplicates
    //     return forkJoin([chatSearch$, studentSearch$]).pipe(
    //         map(([chatResults, studentResults]) => {
    //             const existingStudentIds = new Set(
    //                 chatResults.map((result) => result?.studentId)
    //             );

    //             const filteredStudentResults = studentResults.filter(
    //                 (student) => !existingStudentIds.has(student.studentId)
    //             );

    //             const combinedResults = [
    //                 ...chatResults.map((chat) => ({
    //                     type: 'chat' as const,
    //                     data: chat?.data,
    //                 })),
    //                 ...filteredStudentResults.map((student) => ({
    //                     type: 'student' as const,
    //                     data: student.data,
    //                 })),
    //             ];

    //             return combinedResults;
    //         }),
    //         tap((results) => {
    //             // Cache the results
    //             this.searchCache.set(cacheKey, {
    //                 results: results,
    //                 timestamp: Date.now()
    //             });
    //         }),
    //         catchError((error) => {
    //             console.error('❌ Service: Error in search:', error);
    //             return of([]);
    //         })
    //     );
    // }

    searchMessagesAndStudents(
        searchTerm: string,
        userId: string,
        roomIds?: any[],
    ): Observable<Array<{ type: 'chat' | 'student'; data: any }>> {
        if (!searchTerm.trim()) {
            return of([]);
        }

        const searchTermLower = searchTerm.toLowerCase();
        const cacheKey = `${userId}_${searchTermLower}_${JSON.stringify(roomIds || [])}`;

        // Check cache first
        const cachedResult = this.searchCache.get(cacheKey);
        if (
            cachedResult &&
            Date.now() - cachedResult.timestamp < this.SEARCH_CACHE_DURATION
        ) {
            return of(cachedResult.results);
        }

        // 🔹 Fetch chats where the user is a participant (comprehensive search)
        const chatSearch$ = this.firestore
            .collection<Chat>('chats', (ref) =>
                ref.where(`participants.${userId}`, '==', true),
            )
            .valueChanges()
            .pipe(
                take(1),
                switchMap((chats) => {
                    const filteredChats = chats.filter((chat) => {
                        const studentNameMatches = chat.studentInfo?.name
                            ?.toLowerCase()
                            .includes(searchTermLower);

                        return studentNameMatches;
                    });

                    if (filteredChats.length === 0) {
                        return of([]);
                    }

                    // Get profile images and lastVisibleMessage for each chat participant
                    const chatWithProfileImages$ = filteredChats.map((chat) => {
                        const chatId = chat.chatId;
                        const participantId = chat.studentInfo?.id;

                        if (!participantId) {
                            return of({
                                type: 'chat' as const,
                                data: {
                                    ...chat,
                                    participantProfileImage:
                                        'assets/layout/images/male.png',
                                },
                                studentId: chat.studentInfo?.id,
                            });
                        }

                        // Get lastVisibleMessage from userChats
                        const userChatRef = this.firestore
                            .collection('userChats')
                            .doc(userId)
                            .collection('chats')
                            .doc(chatId);

                        return combineLatest([
                            this.firestore
                                .doc(`users/${participantId}`)
                                .valueChanges()
                                .pipe(take(1)),
                            userChatRef.valueChanges().pipe(take(1)),
                        ]).pipe(
                            map(([userProfile, userChat]: [any, any]) => {                                
                                const photoUid = userProfile?.profile?.photoURL;
                                const status = userProfile?.profile?.status;
                                const lastVisibleMessage =
                                    userChat?.lastVisibleMessage ||
                                    chat.lastMessage;

                                return {
                                    type: 'chat' as const,
                                    data: {
                                        ...chat,
                                        lastVisibleMessage,
                                        participantProfileImage:
                                            photoUid ||
                                            'assets/layout/images/male.png',
                                        status : status
                                    },
                                    studentId: chat.studentInfo?.id,
                                };
                            }),
                            catchError(() =>
                                of({
                                    type: 'chat' as const,
                                    data: {
                                        ...chat,
                                        participantProfileImage:
                                            'assets/layout/images/male.png',
                                    },
                                    studentId: chat.studentInfo?.id,
                                }),
                            ),
                        );
                    });

                    return forkJoin(chatWithProfileImages$);
                }),
                catchError((error) => {
                    console.error('❌ Service: Error fetching chats:', error);
                    return of([]);
                }),
            );
        const currentUser = this.webStorage.getUserData();
        // 🔹 Fetch students if the user is a staff member
        const studentSearch$ = userId.startsWith('staff_')
            ? (() => {
                  // Build query parameters for comprehensive search
                  const queryParams: any = {
                      'fullName.contains': searchTerm,
                      'schoolId.equals': currentUser.schoolId,
                      sort: 'fullName,asc',
                      'profileStatus.notEquals': 'I',
                      // Removed size limit to allow comprehensive search
                  };

                  // Add room filtering for staff users (not admins)
                  if (
                      roomIds &&
                      roomIds.length > 0 &&
                      !Common.isAdminUserLogin(this.webStorage)
                  ) {
                      queryParams['roomId.in'] = roomIds;
                  }

                  return this.lookupService.queryChatStudent(queryParams).pipe(
                      map((response: HttpResponse<any[]>) => {
                          const students = response.body || [];

                          return students.map((student) => {
                              const studentId = `student_${student.id}`;
                              const photoUuid =
                                  student.photoUuid || student.photoURL;

                              return {
                                  type: 'student' as const,
                                  data: {
                                      id: studentId,
                                      profile: {
                                          name:
                                              student.name ||
                                              student.fullName ||
                                              '',
                                          photoURL:
                                              photoUuid ||
                                              'assets/layout/images/male.png',
                                          photoUid: photoUuid, // Keep the original photoUid for later processing
                                          status: student.profileStatus,
                                      },
                                      guardians: [...student.guardian],
                                      staffDTOList: [...student.staffDTOList],
                                      roomId: student.roomId,
                                  },
                                  studentId: studentId,
                              };
                          });
                      }),
                      catchError((error) => {
                          console.error(
                              '❌ Service: Error fetching students:',
                              error,
                          );
                          return of([]);
                      }),
                  );
              })()
            : of([]);

        // 🔹 Combine both searches and remove duplicates
        return forkJoin([chatSearch$, studentSearch$]).pipe(
            map(([chatResults, studentResults]) => {
                const existingStudentIds = new Set(
                    chatResults.map((result) => result?.studentId),
                );

                const filteredStudentResults = studentResults.filter(
                    (student) => !existingStudentIds.has(student.studentId),
                );

                const combinedResults = [
                    ...chatResults.map((chat) => ({
                        type: 'chat' as const,
                        data: chat?.data,
                    })),
                    ...filteredStudentResults.map((student) => ({
                        type: 'student' as const,
                        data: student.data,
                    })),
                ];

                return combinedResults;
            }),
            tap((results) => {
                // Cache the results
                this.searchCache.set(cacheKey, {
                    results: results,
                    timestamp: Date.now(),
                });
            }),
            catchError((error) => {
                console.error('❌ Service: Error in search:', error);
                return of([]);
            }),
        );
    }

    // Method to get chat by ID
    getChatById(chatId: string): Observable<ChatWithUser | null> {
        return this.firestore
            .doc<Chat>(`chats/${chatId}`)
            .valueChanges()
            .pipe(
                switchMap((chat) => {
                    if (!chat) {
                        return of(null);
                    }

                    // For student chats, the "other user" should be the student
                    if (chat.studentInfo) {
                        // Get staff and parent IDs from participants
                        const staffIds = Object.keys(chat.participants)
                            .filter((id) => id.startsWith('staff_'))
                            .map((id) => id.replace('staff_', ''));

                        const parentIds = Object.keys(chat.participants)
                            .filter((id) => id.startsWith('parent_'))
                            .map((id) => id.replace('parent_', ''));

                        // Fetch staff and parent details
                        const staffObservables = staffIds.map((id) =>
                            this.firestore
                                .doc(`users/staff_${id}`)
                                .valueChanges()
                                .pipe(
                                    take(1),
                                    map((staff: any) => ({
                                        id: parseInt(id),
                                        name: staff?.profile?.name || '',
                                        email: staff?.profile?.email || '',
                                        role: 'staff',
                                        photoUrl:
                                            staff?.profile?.photoURL ||
                                            'assets/layout/images/male.png',
                                        createdAt: Date.now(),
                                        status:
                                            staff?.profile?.status || 'offline',
                                        roomIds: staff?.profile?.roomIds || [],
                                    })),
                                ),
                        );

                        const parentObservables = parentIds.map((id) =>
                            this.firestore
                                .doc(`users/parent_${id}`)
                                .valueChanges()
                                .pipe(
                                    take(1),
                                    map((parent: any) => ({
                                        id: parseInt(id),
                                        name: parent?.profile?.name || '',
                                        email: parent?.profile?.email || '',
                                        role: 'parent',
                                        photoUrl:
                                            parent?.profile?.photoURL ||
                                            'assets/layout/images/male.png',
                                        createdAt: Date.now(),
                                        status:
                                            parent?.profile?.status ||
                                            'offline',
                                        roomId: parent?.profile?.roomId,
                                    })),
                                ),
                        );

                        return forkJoin([
                            forkJoin(staffObservables),
                            forkJoin(parentObservables),
                        ]).pipe(
                            switchMap(([staffDetails, parentDetails]) => {
                                return this.firestore
                                    .doc<FirestoreUser>(
                                        `users/${chat.studentInfo.id}`,
                                    )
                                    .valueChanges()
                                    .pipe(
                                        take(1),
                                        map((user) => {
                                            if (!user) {
                                                return null;
                                            }

                                            return {
                                                chatId: chat.chatId,
                                                lastMessage: {
                                                    text:
                                                        chat.lastMessage
                                                            ?.text || '',
                                                    senderId:
                                                        chat.lastMessage
                                                            ?.senderId || '',
                                                    timestamp:
                                                        chat.lastMessage
                                                            ?.timestamp ||
                                                        Date.now(),
                                                    isDeleted:
                                                        chat.lastMessage
                                                            ?.isDeleted ||
                                                        false,
                                                },
                                                unreadCount: 0,
                                                studentInfo: chat.studentInfo,
                                                staffInfo: chat.staffInfo,
                                                otherUser: {
                                                    id: user.id,
                                                    name: user.profile.name,
                                                    email: user.profile.email,
                                                    role: user.profile.role,
                                                    photoUrl:
                                                        user.profile.photoURL,
                                                    createdAt: Date.now(),
                                                    status: user.profile.status,
                                                    guardians:
                                                        parentDetails || [],
                                                    staffDTOList:
                                                        staffDetails || [],
                                                    roomId: chat.studentInfo
                                                        .roomId,
                                                },
                                            };
                                        }),
                                    );
                            }),
                        );
                    }

                    // For staff chats or other types, get the other user's ID (either staff or student)
                    const otherUserId = Object.keys(chat.participants).find(
                        (id) => id !== this.currentUserId,
                    );
                    if (!otherUserId) {
                        return of(null);
                    }

                    // Get the other user's profile
                    return this.firestore
                        .doc<FirestoreUser>(`users/${otherUserId}`)
                        .valueChanges()
                        .pipe(
                            map((user) => {
                                if (!user) {
                                    return null;
                                }

                                return {
                                    chatId: chat.chatId,
                                    lastMessage: {
                                        text: chat.lastMessage?.text || '',
                                        senderId:
                                            chat.lastMessage?.senderId || '',
                                        timestamp:
                                            chat.lastMessage?.timestamp ||
                                            Date.now(),
                                        isDeleted:
                                            chat.lastMessage?.isDeleted ||
                                            false,
                                    },
                                    unreadCount: 0,
                                    studentInfo: chat.studentInfo,
                                    staffInfo: chat.staffInfo,
                                    otherUser: {
                                        id: user.id,
                                        name: user.profile.name,
                                        email: user.profile.email,
                                        role: user.profile.role,
                                        photoUrl: user.profile.photoURL,
                                        createdAt: Date.now(),
                                        status: user.profile.status,
                                    },
                                };
                            }),
                        );
                }),
            );
    }

    markChatAsRead(chatId: string, userId: string): Observable<void> {
        return this.getChatMessages(chatId).pipe(
            take(1),
            switchMap((messages) => {
                const unreadMessages = messages.filter((message) => {
                    if (message.senderId === userId) return false;
                    if (!message.readBy) return true;
                    return !message.readBy[userId];
                });

                if (unreadMessages.length === 0) {
                    return of(undefined);
                }

                const updates = unreadMessages.map((message) => {
                    const updatedReadBy = message.readBy
                        ? { ...message.readBy, [userId]: true }
                        : { [userId]: true };
                    return this.updateMessage(chatId, message.id, {
                        readBy: updatedReadBy,
                    });
                });

                return forkJoin(updates).pipe(map(() => undefined));
            }),
        );
    }

    // Get user details
    getUser(userId: string): Observable<ChatUser | null> {
        if (!userId) return of(null);

        return this.firestore
            .doc<FirestoreUser>(`users/${userId}`)
            .valueChanges()
            .pipe(
                map((user) => {
                    if (!user) return null;
                    return {
                        id: user.id,
                        name: user.profile.name,
                        email: user.profile.email,
                        role: user.profile.role,
                        photoUrl: user.profile.photoURL,
                        lastSeen: user.profile.lastSeen,
                        status: user.profile.status,
                    };
                }),
            );
    }

    formatMessageDate(timestamp: number): string {
        const today = moment().tz(Common.timeZone);
        const messageDate = moment(timestamp).tz(Common.timeZone);

        if (today.isSame(messageDate, 'day')) {
            return 'Today';
        } else if (
            today.clone().subtract(1, 'day').isSame(messageDate, 'day')
        ) {
            return 'Yesterday';
        } else {
            return messageDate.format('DD MMM, YYYY');
        }
    }

    /**
     * Updates multiple students' room information and adjusts chat participants accordingly in bulk.
     * Optimized to minimize Firestore reads/writes and avoid redundant operations.
     * @param students Array of student data objects (must include id and roomId)
     * @param newRoomId The new room ID for all students
     * @param schoolId The school ID
     */
    async updateStudentsRoomBulk(
        students: any[],
        newRoomId: number,
        schoolId: number,
    ): Promise<void> {
        if (!students || students.length === 0) return;

        try {
            // 1. Pre-fetch staff for the new room (cached for this session)
            const newRoomStaff = await this.getStaffByRoomId(
                newRoomId,
                schoolId,
            );
            const prevRoomStaffCache = new Map<number, string[]>();

            // Process students in small chunks to stay within Firestore batch limits (500 ops)
            // Each student update can involve many chat participant updates.
            for (let i = 0; i < students.length; i += 5) {
                const chunk = students.slice(i, i + 5);
                const batch = this.firestore.firestore.batch();
                let hasUpdatesInBatch = false;

                for (const studentData of chunk) {
                    const studentId = `student_${studentData.id}`;
                    const previousRoomId = studentData.roomId;

                    if (previousRoomId === newRoomId) continue;

                    // 1. Update Student Profile in Users collection
                    const userRef = this.firestore.doc(
                        `users/${studentId}`,
                    ).ref;
                    batch.update(userRef, {
                        'profile.name': studentData.fullName,
                        'profile.status':
                            studentData.status === 'A' ? 'online' : 'offline',
                        roomId: newRoomId,
                        updatedAt: Date.now(),
                    });

                    // 2. Fetch student chats (only ONCE for all operations)
                    const chatsSnapshot = await this.firestore
                        .collection<Chat>('chats', (ref) =>
                            ref
                                .where('studentInfo.id', '==', studentId)
                                .where('schoolId', '==', schoolId),
                        )
                        .get()
                        .toPromise();

                    if (chatsSnapshot && !chatsSnapshot.empty) {
                        // Fetch previous room staff if not cached
                        if (
                            previousRoomId &&
                            !prevRoomStaffCache.has(previousRoomId)
                        ) {
                            const staff = await this.getStaffByRoomId(
                                previousRoomId,
                                schoolId,
                            );
                            prevRoomStaffCache.set(previousRoomId, staff);
                        }
                        const previousRoomStaff = previousRoomId
                            ? prevRoomStaffCache.get(previousRoomId) || []
                            : [];
                        const staffToRemove = previousRoomStaff.filter(
                            (id) => !newRoomStaff.includes(id),
                        );

                        for (const chatDoc of chatsSnapshot.docs) {
                            const chatData = chatDoc.data() as Chat;
                            const chatId = chatDoc.id;
                            const participants = {
                                ...(chatData.participants || {}),
                            };

                            // Update main chat doc
                            const chatRef = this.firestore
                                .collection('chats')
                                .doc(chatId).ref;
                            batch.update(chatRef, {
                                'studentInfo.name': studentData.fullName,
                                'info.roomId': newRoomId,
                                'studentInfo.roomId': newRoomId,
                                'info.updatedAt': Date.now(),
                            });

                            // Update userChats for every current participant
                            Object.keys(participants).forEach((pId) => {
                                const userChatRef = this.firestore
                                    .collection('userChats')
                                    .doc(pId)
                                    .collection('chats')
                                    .doc(chatId).ref;
                                batch.update(userChatRef, {
                                    'studentInfo.name': studentData.fullName,
                                    'studentInfo.roomId': newRoomId,
                                    updatedAt: Date.now(),
                                });
                            });

                            // Remove old room staff from this chat
                            staffToRemove.forEach((sId) => {
                                if (participants[sId]) {
                                    delete participants[sId];
                                    const userChatRef = this.firestore
                                        .collection('userChats')
                                        .doc(sId)
                                        .collection('chats')
                                        .doc(chatId).ref;
                                    batch.delete(userChatRef);
                                }
                            });

                            // Add new room staff to this chat
                            newRoomStaff.forEach((sId) => {
                                if (!participants[sId]) {
                                    participants[sId] = true;
                                    const userChatRef = this.firestore
                                        .collection('userChats')
                                        .doc(sId)
                                        .collection('chats')
                                        .doc(chatId).ref;
                                    batch.set(
                                        userChatRef,
                                        {
                                            lastMessage: chatData.lastMessage,
                                            lastVisibleMessage:
                                                chatData.lastMessage,
                                            unreadCount: 1,
                                            studentInfo: {
                                                id: studentId,
                                                name:
                                                    studentData.fullName || '',
                                                roomId: newRoomId,
                                            },
                                            staffInfo: chatData.staffInfo,
                                            updatedAt: Date.now(),
                                        },
                                        { merge: true },
                                    );
                                }
                            });

                            // Sync participants back to the main chat doc
                            batch.update(chatRef, {
                                participants: participants,
                            });
                        }
                    }
                    hasUpdatesInBatch = true;
                }

                if (hasUpdatesInBatch) {
                    await batch.commit();
                }
            }
        } catch (error) {
            console.error('Error in bulk student room update:', error);
            throw error;
        }
    }

    /**
     * Updates student room information and adjusts chat participants accordingly.
     * Consolidates multiple queries into one for performance.
     * @param studentData The updated student data with new roomId
     * @param previousRoomId The previous roomId (optional, for comparison)
     */
    async updateStudentRoom(
        studentData: any,
        previousRoomId?: number,
    ): Promise<void> {
        try {
            const studentId = `student_${studentData.id}`;
            const newRoomId = studentData.roomId;
            const schoolId = studentData.schoolId;

            // If room hasn't changed, no need to update chats (but might need to update profile)
            if (previousRoomId === newRoomId) {
                await this.updateStudentInFirebase(studentData);
                return;
            }

            // Perform a bulk-style update for a single student to leverage optimized path
            // We pass the previousRoomId so the bulk logic identifies the staff to remove.
            const dataForBulk = { ...studentData, roomId: previousRoomId };
            await this.updateStudentsRoomBulk(
                [dataForBulk],
                newRoomId,
                schoolId,
            );
        } catch (error) {
            console.error('Error updating student room:', error);
            throw error;
        }
    }

    /**
     * Gets all staff IDs assigned to a specific room
     * @param roomId The room ID
     * @param schoolId The school ID
     * @returns Array of staff IDs (e.g., ['staff_1', 'staff_2'])
     */
    private async getStaffByRoomId(
        roomId: number,
        schoolId: number,
    ): Promise<string[]> {
        try {
            const response = await lastValueFrom(
                this.lookupService.queryStaff({
                    'schoolId.equals': schoolId,
                    'roomId.in': [roomId],
                    'status.equals': 'A',
                    'designation.notEquals': 'Kiosk',
                }),
            );

            if (response.body) {
                const staffIds: string[] = [];

                // Ensure each staff exists in Firebase and collect their IDs
                for (const staff of response.body) {
                    try {
                        if (!staff.id) {
                            console.warn(
                                'Staff without ID found, skipping:',
                                staff,
                            );
                            continue;
                        }

                        // Create or update staff user in Firebase
                        await this.createOrUpdateUser({
                            id: staff.id,
                            name:
                                staff.name ||
                                `${staff['firstName'] || ''} ${staff['lastName'] || ''}`.trim(),
                            email: staff.email || `${staff.id}@example.com`,
                            role: 'staff',
                            photoUrl: staff.photoUuid,
                            schoolId: schoolId,
                            roomId: 0, // Staff don't have single roomId
                            roomIds:
                                staff.rooms?.map(
                                    (room: any) => room.id || room,
                                ) || [],
                            phoneNumber: staff.phoneNumber,
                            employeeId: staff.employeeId,
                            status: staff.status,
                        });

                        staffIds.push(`staff_${staff.id}`);
                    } catch (userError) {
                        console.error(
                            `Error creating/updating staff user ${staff.id}:`,
                            userError,
                        );
                        // Continue with other staff even if one fails
                    }
                }

                return staffIds;
            }

            return [];
        } catch (error) {
            console.error('Error getting staff by room ID:', error);
            return [];
        }
    }

    formatTime(timestamp: number): string {
        return moment(timestamp).tz(Common.timeZone).format('hh:mm A');
    }

    // Get message time for individual messages
    getMessageTime(timestamp: number): string {
        const messageDate = new Date(timestamp);
        return messageDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    // Add new method for private chat settings
    async togglePrivateChat(
        chatId: string,
        studentId: string,
        parentId: string,
        enable: boolean,
    ): Promise<void> {
        try {
            // -------------------------------
            // Get chat document
            // -------------------------------
            const chatRef = this.firestore.doc(`chats/${chatId}`);
            const chatSnap = await firstValueFrom(chatRef.get());

            if (!chatSnap.exists) {
                throw new Error(`Chat not found: ${chatId}`);
            }

            const chatData = chatSnap.data() as Chat;
            const formattedParentId = `parent_${parentId}`;
            const timestamp = Date.now();

            // -------------------------------
            // Firestore batch (FAST)
            // -------------------------------
            const batch = this.firestore.firestore.batch();

            // Update main chat document
            batch.update(chatRef.ref, {
                'info.isPrivate': enable,
                'info.privateEnabledBy': enable ? formattedParentId : null,
                'info.privateEnabledAt': enable ? timestamp : null,
            });

            // Update userChats for ALL participants
            const participantIds = Object.keys(chatData.participants);

            for (const pid of participantIds) {
                const userChatRef = this.firestore.doc(
                    `userChats/${pid}/chats/${chatId}`,
                );

                batch.set(
                    userChatRef.ref,
                    {
                        isPrivateMode: enable,
                        lastUpdated: timestamp,
                        lastMessage: chatData.lastMessage,
                        studentInfo: chatData.studentInfo,
                        staffInfo: chatData.staffInfo,
                        privateEnabledBy: enable ? formattedParentId : null,
                        privateEnabledAt: enable ? timestamp : null,
                    },
                    { merge: true },
                );
            }

            // -------------------------------
            // Commit batch (ONE network call)
            // -------------------------------
            await batch.commit();
        } catch (error) {
            console.error('Error in togglePrivateChat:', error);
            throw new Error(
                `Failed to toggle private chat: ${
                    error instanceof Error ? error.message : 'Unknown error'
                }`,
            );
        }
    }

    // Add method to get private chat status
    async getPrivateChatStatus(
        studentId: string,
    ): Promise<{ isEnabled: boolean; enabledBy: string }> {
        const settingsRef = this.firestore.doc(
            `userPrivateSettings/student_${studentId}`,
        );
        const settings = await settingsRef.get().toPromise();

        if (settings?.exists) {
            const data = settings.data();
            return {
                isEnabled: (data as any)?.isEnabled || false,
                enabledBy: (data as any)?.enabledBy || '',
            };
        }

        return { isEnabled: false, enabledBy: '' };
    }

    getMessages(chatId: string): Observable<ChatMessage[]> {
        return this.getChatMessages(chatId);
    }

    // Get all users in a chat
    getChatUsers(chatId: string): Observable<ChatUser[]> {
        return this.getChat(chatId).pipe(
            switchMap((chat) => {
                if (!chat || !chat.participants) {
                    return of([]);
                }

                const participantIds = Object.keys(chat.participants);

                const userObservables = participantIds.map((userId) =>
                    this.firestore
                        .doc<{
                            id: string;
                            profile: {
                                name: string;
                                email: string;
                                photoURL: string;
                                status: string;
                                role: string;
                            };
                        }>(`users/${userId}`)
                        .valueChanges()
                        .pipe(
                            map((user) => {
                                if (!user || !user.profile) {
                                    return null;
                                }

                                const chatUser: ChatUser = {
                                    id: userId, // Keep full id like staff_1 or parent_1
                                    name: user.profile.name || 'Unknown User',
                                    email: user.profile.email || '',
                                    role: user.profile.role as
                                        | 'staff'
                                        | 'parent'
                                        | 'student',
                                    photoUrl:
                                        user.profile.photoURL ||
                                        'assets/layout/images/male.png',
                                    lastSeen: Date.now(),
                                    status: user.profile.status as
                                        | 'online'
                                        | 'offline',
                                };

                                return chatUser;
                            }),
                            catchError((error) => {
                                console.error(
                                    `Error fetching user ${userId}:`,
                                    error,
                                );
                                return of(null);
                            }),
                            filter((user): user is ChatUser => user !== null),
                            take(1), // Move take(1) after error handling
                            timeout(1000), // Add timeout to prevent hanging
                            catchError(() => of(null)), // Handle timeout as null
                        ),
                );

                // Use combineLatest instead of forkJoin for better error handling
                return combineLatest(userObservables).pipe(
                    map((users) =>
                        users.filter((user): user is ChatUser => user !== null),
                    ),
                    catchError((error) => {
                        console.error(
                            'Error in combineLatest for getChatUsers:',
                            error,
                        );
                        return of([]);
                    }),
                );
            }),
            catchError((error) => {
                console.error('Error in getChatUsers:', error);
                return of([]);
            }),
        );
    }

    // Add new method for soft deleting messages
    async deleteMessage(
        chatId: string,
        messageId: string,
        currentUserId: string,
    ): Promise<void> {
        try {
            const chatDocRef = this.firestore.doc(`chats/${chatId}`);
            const chatSnap = await chatDocRef.get().toPromise();

            if (!chatSnap?.exists) {
                throw new Error('Chat not found');
            }

            const chat = chatSnap.data() as any;

            if (!chat.messages || !Array.isArray(chat.messages)) {
                throw new Error('No messages found in chat');
            }

            const messageIndex = chat.messages.findIndex(
                (m: any) => m.id === messageId,
            );
            if (messageIndex === -1) {
                throw new Error('Message not found');
            }

            const message = chat.messages[messageIndex];

            if (message.senderId !== currentUserId) {
                throw new Error('You can only delete your own messages');
            }

            // Soft delete the message
            const updatedMessages = [...chat.messages];
            updatedMessages[messageIndex] = {
                ...message,
                isDeleted: true,
                deletedAt: new Date(),
            };

            // Update the chat document with updated messages
            await chatDocRef.update({
                messages: updatedMessages,
            });

            // Check if the deleted message is the last one
            const isLastMessage = messageIndex === chat.messages.length - 1;

            if (isLastMessage) {
                const batch = this.firestore.firestore.batch();
                const participants = Object.keys(chat.participants || {});

                for (const participantId of participants) {
                    const userChatRef = this.firestore
                        .collection('userChats')
                        .doc(participantId)
                        .collection('chats')
                        .doc(chatId).ref;

                    const lastMessageUpdate = {
                        text: message.text,
                        senderId: message.senderId,
                        timestamp: message.timestamp,
                        isDeleted: true,
                    };

                    // Helper function to determine if a participant should see the message
                    const shouldSeeMessage = (
                        participantId: string,
                        message: any,
                    ): boolean => {
                        if (!message.isPrivate) return true;
                        if (participantId.startsWith('staff_')) return true;
                        if (participantId === message.senderId) return true;
                        if (
                            message.targetGuardianId &&
                            participantId ===
                                `parent_${message.targetGuardianId}`
                        )
                            return true;
                        return false;
                    };

                    if (shouldSeeMessage(participantId, message)) {
                        batch.set(
                            userChatRef,
                            {
                                lastMessage: lastMessageUpdate,
                                lastVisibleMessage: lastMessageUpdate,
                                unreadCount: 0,
                                studentInfo: chat.studentInfo,
                                staffInfo: chat.staffInfo,
                            },
                            { merge: true },
                        );
                    } else {
                        batch.set(
                            userChatRef,
                            {
                                lastMessage: lastMessageUpdate,
                                unreadCount: 0,
                                studentInfo: chat.studentInfo,
                                staffInfo: chat.staffInfo,
                            },
                            { merge: true },
                        );
                    }
                }

                await batch.commit();
            }

            // Notify listeners
            this._newMessageSubject.next({
                ...updatedMessages[messageIndex],
                chatId,
            });
        } catch (error) {
            console.error('Error deleting message:', error);
            throw error;
        }
    }

    /**
     * Marks all unread messages in a chat as read for a specific user
     * @param chatId The chat ID
     * @param userId The user ID who read the messages
     */
    async markMessagesAsRead(chatId: string, userId: string): Promise<void> {
        try {
            // Get the chat document
            const chatDoc = await this.firestore
                .collection('chats')
                .doc(chatId)
                .get()
                .toPromise();
            if (!chatDoc?.exists) {
                console.error('Chat not found:', chatId);
                return;
            }

            const chat = chatDoc.data() as Chat;
            const lastMessage = chat.messages?.[chat.messages.length - 1];
            if (!chat.messages || !Array.isArray(chat.messages)) {
                console.error('No messages found in chat:', chatId);
                return;
            }

            // Get current userChat document to get the current unread count
            const userChatDoc = await this.firestore
                .collection('userChats')
                .doc(userId)
                .collection('chats')
                .doc(chatId)
                .get()
                .toPromise();

            const currentUnreadCount =
                userChatDoc?.data()?.['unreadCount'] || 0;

            // Filter messages that need to be marked as read
            const messagesToUpdate = chat.messages.filter(
                (message) =>
                    message.status == 'sent' || message.status == 'delivered',
            );

            if (messagesToUpdate.length === 0) {
                return;
            }

            // Create a batch operation
            const batch = this.firestore.firestore.batch();
            const chatRef = this.firestore.collection('chats').doc(chatId).ref;

            // Update each message's readBy status
            const updatedMessages = chat.messages.map((message) => {
                if (message.status == 'sent' || message.status == 'delivered') {
                    const readBy = message.readBy || {};
                    readBy[userId] = true;

                    // Get all participants
                    const allParticipants = Object.keys(chat.participants);

                    // Check if chat is in private mode
                    if (chat.info?.isPrivate) {
                        // For private chat, only consider relevant participants
                        const relevantParticipants = allParticipants.filter(
                            (participantId) => {
                                // Always include staff members
                                if (participantId.startsWith('staff_')) {
                                    return true;
                                }

                                // For parent messages, only include the sender parent and target parent
                                if (message.senderId.startsWith('parent_')) {
                                    return (
                                        participantId === message.senderId ||
                                        participantId ===
                                            `parent_${message.targetGuardianId}`
                                    );
                                }

                                // For staff messages, include all staff and the target parent
                                if (message.senderId.startsWith('staff_')) {
                                    return (
                                        participantId.startsWith('staff_') ||
                                        participantId ===
                                            `parent_${message.targetGuardianId}`
                                    );
                                }

                                return false;
                            },
                        );

                        // Check if all relevant participants have read the message
                        const allRead = relevantParticipants.every(
                            (participantId) => readBy[participantId],
                        );
                        return {
                            ...message,
                            readBy,
                            status: allRead ? 'read' : 'delivered',
                        };
                    } else {
                        // For non-private chat, check all participants
                        const allRead = allParticipants.every(
                            (participantId) => readBy[participantId],
                        );
                        return {
                            ...message,
                            readBy,
                            status: allRead ? 'read' : 'delivered',
                        };
                    }
                }
                return message;
            });

            // Update the chat document with the new messages array
            batch.update(chatRef, {
                messages: updatedMessages,
                lastMessage: {
                    ...chat.lastMessage,
                    isDeleted: lastMessage?.isDeleted || false,
                    status: updatedMessages[updatedMessages.length - 1].status,
                },
            });

            // Update unread count in userChats
            const userChatRef = this.firestore
                .collection('userChats')
                .doc(userId)
                .collection('chats')
                .doc(chatId).ref;

            // When marking messages as read, set unread count to 0 for this chat
            // since all messages in the chat are being marked as read

            // Calculate unread count based on updated messages
            // const unreadCount = updatedMessages.filter((message) => {
            //     if (chat.info?.isPrivate) {
            //         // For private chat, only count relevant messages
            //         if (message.senderId.startsWith('parent_')) {
            //             return (
            //                 (message.senderId === userId ||
            //                     `parent_${message.targetGuardianId}` ===
            //                         userId) &&
            //                 message.status !== 'read'
            //             );
            //         } else if (message.senderId.startsWith('staff_')) {
            //             return (
            //                 (message.senderId === userId ||
            //                     `parent_${message.targetGuardianId}` ===
            //                         userId) &&
            //                 message.status !== 'read'
            //             );
            //         }
            //         return false;
            //     } else {
            //         // For group chat, count all unread messages
            //         return message.status !== 'read';
            //     }
            // }).length;

            // // Update userChat with new unread count
            // batch.update(userChatRef, {
            //     unreadCount: Math.max(
            //         0,
            //         currentUnreadCount - messagesToUpdate.length
            //     ),

            batch.update(userChatRef, {
                unreadCount: 0,
                lastMessage: {
                    ...chat.lastMessage,
                    isDeleted: lastMessage?.isDeleted || false,
                    status: updatedMessages[updatedMessages.length - 1].status,
                },
            });

            // Commit the batch
            await batch.commit();

            // Emit chat update to notify sidebar of unread count change
            this.emitChatUpdate(chatId, chat.lastMessage, 0);
        } catch (error) {
            console.error('Error marking messages as read:', error);
            throw error;
        }
    }

    getUnreadCount(chatId: string, userId: string): Observable<number> {
        return this.getChatMessages(chatId).pipe(
            map((messages) => {
                return messages.filter((message) => {
                    if (message.senderId === userId) return false;
                    if (!message.readBy) return true;
                    return !message.readBy[userId];
                }).length;
            }),
        );
    }

    // ...existing code...
    async updateMessage(
        chatId: string,
        messageId: string,
        updates: Partial<ChatMessage>,
    ): Promise<void> {
        const chatRef = this.firestore.collection('chats').doc(chatId);
        const snap = await chatRef.get().toPromise();

        if (!snap?.exists) {
            throw new Error('Chat not found');
        }

        const chat = snap.data() as Chat;
        const messages = Array.isArray(chat.messages) ? [...chat.messages] : [];

        const idx = messages.findIndex((m) => m.id === messageId);
        if (idx === -1) {
            throw new Error('Message not found in messages array');
        }

        const now = Date.now();
        const original = messages[idx];

        // Only set isEdited if the text content is being modified
        const isTextEdit =
            updates.text !== undefined && updates.text !== original.text;

        // Merge updates and conditionally set edit flags
        const edited = {
            ...original,
            ...updates,
            ...(isTextEdit && {
                isEdited: true,
                editedAt: updates.editedAt ?? now,
            }),
        };

        messages[idx] = edited; // <-- Update the existing message

        // Optional: keep lastMessage in sync if this is the last message
        const updateData: any = { messages };

        const isLast =
            messages.length > 0 &&
            messages[messages.length - 1]?.id === messageId;

        if (isLast) {
            updateData.lastMessage = {
                ...(chat.lastMessage || {}),
                text: edited.text,
                senderId: edited.senderId ?? original.senderId,
                timestamp: edited.timestamp ?? original.timestamp,
                isDeleted: edited.isDeleted ?? original.isDeleted ?? false,
                status: edited.status ?? original.status ?? 'sent',
                id: edited.id ?? original.id,
            };
        }

        // If this is the last message and text was edited, update userChats for all participants
        if (isLast && isTextEdit) {
            const batch = this.firestore.firestore.batch();
            const participants = Object.keys(chat.participants || {});

            // Update main chat document
            batch.update(chatRef.ref, updateData);

            // Update userChats for all participants
            for (const participantId of participants) {
                const userChatRef = this.firestore
                    .collection('userChats')
                    .doc(participantId)
                    .collection('chats')
                    .doc(chatId).ref;

                // Helper function to determine if a participant should see the message
                const shouldSeeMessage = (
                    participantId: string,
                    message: any,
                ): boolean => {
                    if (!message.isPrivate) return true;
                    if (participantId.startsWith('staff_')) return true;
                    if (participantId === message.senderId) return true;
                    if (
                        message.targetGuardianId &&
                        participantId === `parent_${message.targetGuardianId}`
                    )
                        return true;
                    return false;
                };

                if (shouldSeeMessage(participantId, edited)) {
                    const lastMessageUpdate = {
                        text: edited.text,
                        senderId: edited.senderId,
                        timestamp: edited.timestamp,
                        isDeleted: edited.isDeleted ?? false,
                        isEdited: edited.isEdited,
                        editedAt: edited.editedAt,
                    };

                    const lastVisibleMessageUpdate = {
                        ...lastMessageUpdate,
                        isPrivate: edited.isPrivate ?? false,
                        status: edited.status ?? 'sent',
                    };

                    batch.set(
                        userChatRef,
                        {
                            lastMessage: lastMessageUpdate,
                            lastVisibleMessage: lastVisibleMessageUpdate,
                            studentInfo: chat.studentInfo,
                            staffInfo: chat.staffInfo,
                            updatedAt: now,
                        },
                        { merge: true },
                    );
                } else {
                    // For participants who can't see the message, only update basic info
                    batch.set(
                        userChatRef,
                        {
                            lastMessage: {
                                text: edited.text,
                                senderId: edited.senderId,
                                timestamp: edited.timestamp,
                                isDeleted: edited.isDeleted ?? false,
                                isEdited: edited.isEdited,
                                editedAt: edited.editedAt,
                            },
                            studentInfo: chat.studentInfo,
                            staffInfo: chat.staffInfo,
                            updatedAt: now,
                        },
                        { merge: true },
                    );
                }
            }

            await batch.commit();
        } else {
            // If not the last message or no text edit, just update the main chat
            await chatRef.update(updateData);
        }
    }
    // ...existing code...

    // Search messages across all chats for a user with pagination
    searchMessagesPaginated(
        searchTerm: string,
        userId: string,
        page: number = 0,
        pageSize: number = 10,
    ): Observable<{
        results: { chatId: string; messages: ChatMessage[] }[];
        hasMore: boolean;
        total: number;
    }> {
        if (!searchTerm || searchTerm.trim().length < 2) {
            return of({ results: [], hasMore: false, total: 0 });
        }

        const searchTermLower = searchTerm.toLowerCase().trim();

        return this.firestore
            .collection<Chat>(
                'chats',
                (ref) =>
                    ref.where(`participants.${userId}`, '==', true).limit(50), // Limit chats to search through for performance
            )
            .valueChanges()
            .pipe(
                take(1),
                map((chats) => {
                    const searchResults: {
                        chatId: string;
                        messages: ChatMessage[];
                    }[] = [];

                    chats.forEach((chat) => {
                        if (!chat.messages || !Array.isArray(chat.messages))
                            return;

                        const matchingMessages = chat.messages.filter(
                            (message) =>
                                message.text &&
                                message.text
                                    .toLowerCase()
                                    .includes(searchTermLower) &&
                                !message.isDeleted,
                        );

                        if (matchingMessages.length > 0) {
                            searchResults.push({
                                chatId: chat.chatId,
                                messages: matchingMessages,
                            });
                        }
                    });

                    // Sort by most recent message first
                    searchResults.sort((a, b) => {
                        const aLatest = Math.max(
                            ...a.messages.map((m) => m.timestamp),
                        );
                        const bLatest = Math.max(
                            ...b.messages.map((m) => m.timestamp),
                        );
                        return bLatest - aLatest;
                    });

                    const total = searchResults.length;
                    const startIndex = page * pageSize;
                    const endIndex = startIndex + pageSize;
                    const paginatedResults = searchResults.slice(
                        startIndex,
                        endIndex,
                    );
                    const hasMore = endIndex < total;

                    return {
                        results: paginatedResults,
                        hasMore,
                        total,
                    };
                }),
                catchError((error) => {
                    console.error('Error searching messages:', error);
                    return of({ results: [], hasMore: false, total: 0 });
                }),
            );
    }

    // Legacy method for backward compatibility
    searchMessages(
        searchTerm: string,
        userId: string,
    ): Observable<{ chatId: string; messages: ChatMessage[] }[]> {
        return this.searchMessagesPaginated(searchTerm, userId, 0, 50).pipe(
            map((result) => result.results),
        );
    }

    /**
     * Updates user information in Firebase when a guardian is updated
     * @param guardianData The updated guardian data
     */
    async updateGuardianInFirebase(guardianData: any): Promise<void> {
        try {
            const guardianId = `parent_${guardianData.id}`;
            const batch = this.firestore.firestore.batch();

            // 1. Update user profile in /users collection
            const userRef = this.firestore.doc(`users/${guardianId}`).ref;
            batch.update(userRef, {
                'profile.name': guardianData.fullName,
                'profile.email': guardianData.email.toLowerCase(),
                'profile.photoURL':
                    guardianData.photoUrl || 'assets/layout/images/male.png',
            });

            // // 2. Update all chats where this guardian is a participant
            // const userChatsRef = this.firestore.collection('userChats').doc(guardianId)
            //   .collection('chats');

            // const userChatsSnapshot = await userChatsRef.get().toPromise();

            // if (userChatsSnapshot) {
            //   userChatsSnapshot.forEach((doc: firebase.firestore.QueryDocumentSnapshot) => {
            //     const chatRef = userChatsRef.doc(doc.id).ref;
            //     batch.update(chatRef, {
            //       'staffInfo.name': guardianData.fullName
            //     });
            //   });
            // }

            // // 3. Update all chats in the main chats collection
            // const chatsRef = this.firestore.collection('chats');
            // const chatsQuery = chatsRef.ref.where(`participants.${guardianId}`, '==', true);
            // const chatsSnapshot = await chatsQuery.get();

            // if (chatsSnapshot) {
            //   chatsSnapshot.forEach((doc: firebase.firestore.QueryDocumentSnapshot) => {
            //     const chatRef = chatsRef.doc(doc.id).ref;
            //     batch.update(chatRef, {
            //       'staffInfo.name': guardianData.fullName
            //     });
            //   });
            // }

            // Commit all updates
            await batch.commit();
        } catch (error) {
            console.error('Error updating guardian in Firebase:', error);
        }
    }

    /**
     * Updates student information in Firebase when a student is updated
     * @param studentData The updated student data
     */
    async updateStudentInFirebase(studentData: any): Promise<void> {
        try {
            const studentId = `student_${studentData.id}`;
            const batch = this.firestore.firestore.batch();

            // 1. Update /users/{studentId}
            const userRef = this.firestore.doc(`users/${studentId}`).ref;
            batch.update(userRef, {
                'profile.name': studentData.fullName,
                'profile.email':
                    studentData.email?.toLowerCase() ||
                    `${studentId}@example.com`,
                'profile.photoURL':
                    studentData.photoUuid || 'assets/layout/images/male.png',
                'profile.status':
                    studentData.status === 'A' ? 'online' : 'offline',
            });

            // 2. Update /chats where student is referenced
            const chatsRef = this.firestore.collection('chats');
            const chatsQuery = chatsRef.ref.where(
                'studentInfo.id',
                '==',
                studentId,
            );
            const chatsSnapshot = await chatsQuery.get();

            if (!chatsSnapshot.empty) {
                for (const chatDoc of chatsSnapshot.docs) {
                    const chatData = chatDoc.data() as any;
                    const chatId = chatDoc.id;

                    // 2a. Update /chats/{chatId}
                    const chatRef = chatsRef.doc(chatId).ref;
                    batch.update(chatRef, {
                        'studentInfo.name': studentData.fullName,
                    });

                    // 2b. Update each /userChats/{userId}/chats/{chatId}
                    const participantsMap =
                        (chatData as any).participants || {};
                    const participantIds = Object.keys(participantsMap); // Convert { staff_1138: true, ... } → [ 'staff_1138', ... ]

                    for (const userId of participantIds) {
                        const userChatRef = this.firestore
                            .collection('userChats')
                            .doc(userId)
                            .collection('chats')
                            .doc(chatId).ref;

                        batch.update(userChatRef, {
                            'studentInfo.name': studentData.fullName,
                        });
                    }
                }
            }

            // 3. Commit batched writes
            await batch.commit();
        } catch (error) {
            console.error('Error updating student in Firebase:', error);
        }
    }

    /**
     * Updates student profile information in Firebase when a student profile is patched
     * @param studentData The patched student data
     */
    async patchStudentProfileInFirebase(studentData: any): Promise<void> {
        try {
            const studentId = `student_${studentData.id}`;
            const batch = this.firestore.firestore.batch();

            // 1. Update user profile in /users collection
            const userRef = this.firestore.doc(`users/${studentId}`).ref;
            const updates: any = {
                'profile.name': studentData.fullName,
                'profile.status':
                    studentData.status === 'A' ? 'online' : 'offline',
            };

            if (studentData.roomId) {
                updates['roomId'] = studentData.roomId;
            }

            batch.update(userRef, updates);

            // 2. Update all chats in the main chats collection
            const chatsRef = this.firestore.collection('chats');
            const chatsQuery = chatsRef.ref.where(
                'studentInfo.id',
                '==',
                studentId,
            );
            const chatsSnapshot = await chatsQuery.get();

            if (!chatsSnapshot.empty) {
                for (const doc of chatsSnapshot.docs) {
                    const chatData = doc.data() as any;
                    const chatId = doc.id;

                    // 2a. Update in /chats collection
                    const chatRef = chatsRef.doc(chatId).ref;
                    const chatUpdates: any = {
                        'studentInfo.name': studentData.fullName,
                    };
                    if (studentData.roomId) {
                        chatUpdates['studentInfo.roomId'] = studentData.roomId;
                    }
                    batch.update(chatRef, chatUpdates);

                    // 2b. Update each /userChats/{userId}/chats/{chatId}
                    const participantsMap = chatData.participants || {};
                    const participantIds = Object.keys(participantsMap); // ["staff_123", "parent_456", ...]

                    for (const userId of participantIds) {
                        const userChatRef = this.firestore
                            .collection('userChats')
                            .doc(userId)
                            .collection('chats')
                            .doc(chatId).ref;

                        const userChatUpdates: any = {
                            'studentInfo.name': studentData.fullName,
                        };
                        if (studentData.roomId) {
                            userChatUpdates['studentInfo.roomId'] =
                                studentData.roomId;
                        }

                        batch.update(userChatRef, userChatUpdates);
                    }
                }
            }

            // Commit all updates
            await batch.commit();
        } catch (error) {
            console.error('Error patching student profile in Firebase:', error);
        }
    }

    /**
     * Creates a new parent in Firebase and adds them to existing chats of their children
     * Only creates parent in Firebase if at least one child has existing chats
     * @param parentData The new parent data with associated students
     */
    async createParentInFirebase(parentData: any): Promise<void> {
        try {
            const parentId = `parent_${parentData.id}`;

            // 1. Get all associated students (including siblings)
            const studentIds =
                parentData.students
                    ?.map((student: any) =>
                        typeof student === 'object' ? student.id : student,
                    )
                    .filter((id: any) => id !== undefined) || [];

            if (studentIds.length === 0) {
                return;
            }

            // 2. Check if any students have existing chats
            let hasExistingChats = false;
            const chatsToUpdate: any[] = [];

            for (const studentId of studentIds) {
                const studentIdStr = `student_${studentId}`;

                const studentChatsSnapshot = await this.firestore
                    .collection<Chat>('chats', (ref) =>
                        ref
                            .where('studentInfo.id', '==', studentIdStr)
                            .where('schoolId', '==', parentData.schoolId),
                    )
                    .get()
                    .toPromise();

                if (studentChatsSnapshot && !studentChatsSnapshot.empty) {
                    hasExistingChats = true;

                    for (const chatDoc of studentChatsSnapshot.docs) {
                        const chatData = chatDoc.data() as Chat;
                        const chatId = chatDoc.id;

                        // Check if parent is already a participant
                        const participants = chatData.participants || {};
                        if (!participants[parentId]) {
                            chatsToUpdate.push({ chatId, chatData });
                        } else {
                            console.log(
                                ` Parent ${parentId} already in chat ${chatId}`,
                            );
                        }
                    }
                } else {
                    console.log(` No chats found for student ${studentIdStr}`);
                }
            }

            // 3. Only proceed with Firebase creation if there are existing chats
            if (!hasExistingChats || chatsToUpdate.length === 0) {
                return;
            }

            const batch = this.firestore.firestore.batch();

            // 4. Create parent user in /users collection
            const userRef = this.firestore.doc(`users/${parentId}`).ref;
            const userProfile = {
                name:
                    parentData.fullName ||
                    `${parentData.firstname} ${parentData.lastname}`,
                email:
                    parentData.email?.toLowerCase() ||
                    `${parentId}@example.com`,
                photoURL: 'assets/layout/images/male.png',
                lastSeen: Date.now(),
                status: 'online',
                role: 'parent',
            };

            const userDataToStore = {
                id: parentId,
                profile: userProfile,
                schoolId: parentData.schoolId,
                role: 'parent',
                roomId: 0, // Parents don't have specific rooms
                roomIds: [],
                updatedAt: Date.now(),
                createdAt: Date.now(),
            };

            batch.set(userRef, userDataToStore);

            // 5. Add parent to all found chats
            for (const { chatId, chatData } of chatsToUpdate) {
                const chatRef = this.firestore
                    .collection('chats')
                    .doc(chatId).ref;
                const participants = { ...(chatData.participants || {}) };
                participants[parentId] = true;

                batch.update(chatRef, {
                    participants: participants,
                    'info.updatedAt': Date.now(),
                });

                // Create userChat entry for the parent
                const userChatRef = this.firestore
                    .collection('userChats')
                    .doc(parentId)
                    .collection('chats')
                    .doc(chatId).ref;

                const userChatData = {
                    lastMessage: chatData.lastMessage,
                    lastVisibleMessage: chatData.lastMessage,
                    unreadCount: 0,
                    studentInfo: chatData.studentInfo,
                    staffInfo: chatData.staffInfo,
                    updatedAt: Date.now(),
                };

                batch.set(userChatRef, userChatData, { merge: true });
            }

            // 6. Commit all changes
            await batch.commit();
        } catch (error) {
            console.error('❌ Error creating parent in Firebase:', error);
            throw error;
        }
    }

    /**
     * Removes a parent from all chats and deletes their Firebase user
     * @param parentData The parent data to delete
     */
    async deleteParentFromFirebase(parentData: any): Promise<void> {
        try {
            const parentId = `parent_${parentData.id}`;
            const batch = this.firestore.firestore.batch();

            // 1. Find all chats where this parent is a participant
            const parentChatsSnapshot = await this.firestore
                .collection<Chat>('chats', (ref) =>
                    ref
                        .where(`participants.${parentId}`, '==', true)
                        .where('schoolId', '==', parentData.schoolId),
                )
                .get()
                .toPromise();

            if (parentChatsSnapshot && !parentChatsSnapshot.empty) {
                for (const chatDoc of parentChatsSnapshot.docs) {
                    const chatData = chatDoc.data() as Chat;
                    const chatId = chatDoc.id;
                    const participants = { ...(chatData.participants || {}) };

                    // Remove parent from participants
                    delete participants[parentId];

                    const chatRef = this.firestore
                        .collection('chats')
                        .doc(chatId).ref;
                    batch.update(chatRef, {
                        participants: participants,
                        'info.updatedAt': Date.now(),
                    });

                    // Remove from userChats
                    const userChatRef = this.firestore
                        .collection('userChats')
                        .doc(parentId)
                        .collection('chats')
                        .doc(chatId).ref;

                    batch.delete(userChatRef);
                }
            } else {
                console.log('ℹ No chats found for parent');
            }

            // 2. Delete parent user from /users collection
            const userRef = this.firestore.doc(`users/${parentId}`).ref;
            batch.delete(userRef);

            // 3. Delete entire userChats collection for this parent
            const userChatsRef = this.firestore
                .collection('userChats')
                .doc(parentId);
            const userChatsSnapshot = await userChatsRef
                .collection('chats')
                .get()
                .toPromise();

            if (userChatsSnapshot && !userChatsSnapshot.empty) {
                userChatsSnapshot.docs.forEach((doc) => {
                    batch.delete(doc.ref);
                });
            }

            // 4. Commit all changes
            await batch.commit();
        } catch (error) {
            console.error('❌ Error deleting parent from Firebase:', error);
            throw error;
        }
    }

    /**
     * Updates staff information in Firebase when a staff member is updated
     * @param staffData The updated staff data
     */
    async updateStaffInFirebase(staffData: any): Promise<void> {
        try {
            const staffId = `staff_${staffData.id}`;
            const batch = this.firestore.firestore.batch();

            // Step 1: Update /users/{staffId}
            const userRef = this.firestore.doc(`users/${staffId}`).ref;
            batch.update(userRef, {
                'profile.name': staffData.name,
                'profile.email':
                    staffData.email?.toLowerCase() || `${staffId}@example.com`,
                'profile.photoURL':
                    staffData.photoUuid || 'assets/layout/images/male.png',
                'profile.status':
                    staffData.status === 'A' ? 'online' : 'offline',
            });

            // Step 2: Update /chats and userChats
            const chatsRef = this.firestore.collection('chats');
            const chatsQuery = chatsRef.ref.where(
                `participants.${staffId}`,
                '==',
                true,
            );
            const chatsSnapshot = await chatsQuery.get();

            if (!chatsSnapshot.empty) {
                for (const chatDoc of chatsSnapshot.docs) {
                    const chatData = chatDoc.data() as any;
                    const chatId = chatDoc.id;

                    // 2a: Update chats
                    const chatRef = chatsRef.doc(chatId).ref;
                    if (chatData?.['staffInfo']?.['id'] === staffId) {
                        batch.update(chatRef, {
                            'staffInfo.name': staffData.name,
                        });
                    }

                    // 2b: Update userChats for all participants
                    const participantsMap = chatData.participants || {};
                    const participantIds = Object.keys(participantsMap);

                    for (const userId of participantIds) {
                        const userChatDocRef = this.firestore
                            .collection('userChats')
                            .doc(userId)
                            .collection('chats')
                            .doc(chatId);

                        const userChatDocSnap = await userChatDocRef
                            .get()
                            .toPromise();
                        const userChatData = userChatDocSnap?.data();

                        if (userChatData?.['staffInfo']?.['id'] === staffId) {
                            batch.update(userChatDocRef.ref, {
                                'staffInfo.name': staffData.name,
                            });
                        }
                    }
                }
            }

            // Step 3: Update /staffChats
            const staffChatsRef = this.firestore.collection('staffChats');
            const staffChatsSnapshot = await staffChatsRef.get().toPromise();

            if (staffChatsSnapshot && !staffChatsSnapshot.empty) {
                for (const doc of staffChatsSnapshot.docs) {
                    const chatData = doc.data() as any;
                    const chatRef = staffChatsRef.doc(doc.id).ref;

                    if (chatData?.['staffInfo']?.['id'] === staffId) {
                        batch.update(chatRef, {
                            'staffInfo.name': staffData.name,
                        });
                    }

                    if (chatData?.['staff2Info']?.['id'] === staffId) {
                        batch.update(chatRef, {
                            'staff2Info.name': staffData.name,
                        });
                    }
                }
            }

            // Step 4: Update /staffUserChats for all participants of each staffChat
            for (const doc of staffChatsSnapshot!.docs) {
                const chatId = doc.id;
                const chatData = doc.data() as any;

                const participantIds = [
                    chatData?.['staffInfo']?.['id'],
                    chatData?.['staff2Info']?.['id'],
                ].filter(Boolean);

                for (const participantId of participantIds) {
                    const chatRef = this.firestore
                        .collection('staffUserChats')
                        .doc(participantId)
                        .collection('staffChats')
                        .doc(chatId);

                    const chatSnap = await chatRef.get().toPromise();
                    const chatContent = chatSnap?.data();

                    if (!chatContent) continue;

                    const updates: any = {};

                    if (chatContent?.['staffInfo']?.['id'] === staffId) {
                        updates['staffInfo.name'] = staffData.name;
                    }

                    if (chatContent?.['staff2Info']?.['id'] === staffId) {
                        updates['staff2Info.name'] = staffData.name;
                    }

                    if (Object.keys(updates).length > 0) {
                        batch.update(chatRef.ref, updates);
                    }
                }
            }

            // ✅ Commit all updates
            await batch.commit();
        } catch (error) {
            console.log(' Error updating staff in Firebase:', error);
        }
    }

    async updateStudentProfileInFirebase(studentData: any): Promise<void> {
        try {
            const studentId = `student_${studentData.id}`;
            const batch = this.firestore.firestore.batch();

            // 1. Update /users/{studentId}
            const userRef = this.firestore.doc(`users/${studentId}`).ref;
            batch.update(userRef, {
                'profile.photoURL':
                    studentData.photoUuid || 'assets/layout/images/male.png',
            });

            // 3. Commit batched writes
            await batch.commit();
        } catch (error) {
            console.error('Error updating student in Firebase:', error);
        }
    }

    async updateStaffProfileInFirebase(staffData: any): Promise<void> {
        try {
            const staffId = `staff_${staffData.id}`;
            const batch = this.firestore.firestore.batch();

            // 1. Update /users/{studentId}
            const userRef = this.firestore.doc(`users/${staffId}`).ref;
            batch.update(userRef, {
                'profile.photoURL':
                    staffData.photoUuid || 'assets/layout/images/male.png',
            });

            // 3. Commit batched writes
            await batch.commit();
        } catch (error) {
            console.error('Error updating student in Firebase:', error);
        }
    }

    /**
     * Ensures that a staff user exists in the Firestore users collection
     * @param staffData The staff data to check/create
     * @returns Promise<boolean> indicating if user was created or already exists
     */
    private async ensureStaffUserExists(staffData: any): Promise<boolean> {
        try {
            const staffId = `staff_${staffData.id}`;
            const userRef = this.firestore.doc(`users/${staffId}`);

            const userDoc = await userRef.get().toPromise();

            if (!userDoc?.exists) {
                // Create the user in Firestore users collection
                const userProfile = {
                    name:
                        staffData.name ||
                        staffData.firstName + ' ' + staffData.lastName,
                    email: staffData.email || `${staffId}@example.com`,
                    photoURL:
                        staffData.photoUuid || 'assets/layout/images/male.png',
                    lastSeen: Date.now(),
                    status: 'online',
                    role: 'staff',
                };

                const userDataToStore = {
                    id: staffId,
                    profile: userProfile,
                    schoolId: staffData.schoolId,
                    role: 'staff',
                    roomId: staffData.primaryRoomId || 0,
                    roomIds:
                        staffData.rooms?.map((room: any) => room.id || room) ||
                        [],
                    updatedAt: Date.now(),
                    createdAt: Date.now(),
                };

                await userRef.set(userDataToStore, { merge: true });
                return true;
            }

            return false; // User already exists
        } catch (error) {
            console.error('Error ensuring staff user exists:', error);
            throw error;
        }
    }

    /**
     * Updates chat participants when staff is updated - ensures staff is in all chats for their assigned rooms
     * @param staffData The updated staff data with current room assignments
     * @param previousRooms The previous room assignments (optional, for comparison)
     */
    async updateChatParticipantsForStaffRooms(
        staffData: any,
        previousRooms?: any[],
    ): Promise<void> {
        try {
            const staffId = `staff_${staffData.id}`;

            // Debug the incoming staffData structure

            // Extract current room IDs from updated staff data - handle different possible structures
            let newRoomIds: number[] = [];
            if (staffData.rooms && Array.isArray(staffData.rooms)) {
                newRoomIds = staffData.rooms
                    .map((room: any) => {
                        if (typeof room === 'number') return room;
                        if (typeof room === 'string') return parseInt(room);
                        if (room && typeof room === 'object' && room.id)
                            return room.id;
                        return room; // fallback
                    })
                    .filter((id: any) => id !== undefined && id !== null);
            }

            // Get current room IDs from Firestore users collection
            const userDoc = await this.firestore
                .doc(`users/${staffId}`)
                .get()
                .toPromise();
            const userData = userDoc?.data() as any;
            const currentFirestoreRoomIds = userData?.roomIds || [];

            // Use Firestore data as the source of truth for previous rooms
            const previousRoomIds = currentFirestoreRoomIds;

            // Find rooms that were added and removed for reference
            const addedRooms = newRoomIds.filter(
                (roomId: number) => !previousRoomIds.includes(roomId),
            );
            const removedRooms = previousRoomIds.filter(
                (roomId: number) => !newRoomIds.includes(roomId),
            );

            const batch = this.firestore.firestore.batch();
            let hasUpdates = false;

            // Ensure staff user exists in users collection
            await this.ensureStaffUserExists(staffData);

            // Only check ADDED rooms and ensure staff is in all chats for those rooms
            for (const roomId of addedRooms) {
                // Try with number first, then string as fallback
                let chatsSnapshot = await this.firestore
                    .collection<Chat>('chats', (ref) =>
                        ref
                            .where('info.roomId', '==', roomId)
                            .where('schoolId', '==', staffData.schoolId),
                    )
                    .get()
                    .toPromise();

                // If no results with number, try with string
                if (!chatsSnapshot || chatsSnapshot.empty) {
                    chatsSnapshot = await this.firestore
                        .collection<Chat>('chats', (ref) =>
                            ref
                                .where('info.roomId', '==', roomId.toString())
                                .where('schoolId', '==', staffData.schoolId),
                        )
                        .get()
                        .toPromise();
                }

                if (chatsSnapshot && !chatsSnapshot.empty) {
                    for (const chatDoc of chatsSnapshot.docs) {
                        const chatData = chatDoc.data() as Chat;
                        const chatId = chatDoc.id;

                        const participants = {
                            ...(chatData.participants || {}),
                        };

                        // Add staff to participants if not already present
                        if (!participants[staffId]) {
                            participants[staffId] = true;

                            // Update main chat document
                            const chatRef = this.firestore
                                .collection('chats')
                                .doc(chatId).ref;
                            batch.update(chatRef, {
                                participants: participants,
                                'info.updatedAt': Date.now(),
                            });

                            // Update cursor to include new staff
                            const cursor = { ...(chatData.cursor || {}) };
                            cursor[staffId] = chatData.messages?.length
                                ? chatData.messages[
                                      chatData.messages.length - 1
                                  ].id
                                : '';

                            batch.update(chatRef, {
                                cursor: cursor,
                            });

                            // Create userChats entry for the staff
                            const userChatRef = this.firestore
                                .collection('userChats')
                                .doc(staffId)
                                .collection('chats')
                                .doc(chatId).ref;

                            const userChatData = {
                                lastMessage: chatData.lastMessage,
                                lastVisibleMessage: chatData.lastMessage,
                                unreadCount: 0,
                                studentInfo: chatData.studentInfo,
                                staffInfo: chatData.staffInfo,
                                updatedAt: Date.now(),
                            };

                            batch.set(userChatRef, userChatData, {
                                merge: true,
                            });

                            hasUpdates = true;
                        }
                    }
                }
            }

            // Handle removed rooms - remove staff from chats in these rooms
            for (const roomId of removedRooms) {
                // Try with number first, then string as fallback
                let chatsSnapshot = await this.firestore
                    .collection<Chat>('chats', (ref) =>
                        ref
                            .where('info.roomId', '==', roomId)
                            .where('schoolId', '==', staffData.schoolId),
                    )
                    .get()
                    .toPromise();

                // If no results with number, try with string
                if (!chatsSnapshot || chatsSnapshot.empty) {
                    chatsSnapshot = await this.firestore
                        .collection<Chat>('chats', (ref) =>
                            ref
                                .where('info.roomId', '==', roomId.toString())
                                .where('schoolId', '==', staffData.schoolId),
                        )
                        .get()
                        .toPromise();
                }

                if (chatsSnapshot && !chatsSnapshot.empty) {
                    for (const chatDoc of chatsSnapshot.docs) {
                        const chatData = chatDoc.data() as Chat;
                        const chatId = chatDoc.id;
                        const participants = {
                            ...(chatData.participants || {}),
                        };

                        // Remove staff from participants if present
                        if (participants[staffId]) {
                            delete participants[staffId];

                            // Update main chat document
                            const chatRef = this.firestore
                                .collection('chats')
                                .doc(chatId).ref;
                            batch.update(chatRef, {
                                participants: participants,
                                'info.updatedAt': Date.now(),
                            });

                            // Update cursor to remove staff
                            const cursor = { ...(chatData.cursor || {}) };
                            delete cursor[staffId];

                            batch.update(chatRef, {
                                cursor: cursor,
                            });

                            // Remove from userChats
                            const userChatRef = this.firestore
                                .collection('userChats')
                                .doc(staffId)
                                .collection('chats')
                                .doc(chatId).ref;

                            batch.delete(userChatRef);

                            hasUpdates = true;
                        }
                    }
                }
            }

            // REMOVED scan of allStaffChatsSnapshot for performance

            // Update user's roomIds in the users collection if rooms were added or removed
            if (
                addedRooms.length > 0 ||
                removedRooms.length > 0 ||
                staffData.primaryRoomId
            ) {
                const userRef = this.firestore.doc(`users/${staffId}`).ref;
                const userUpdates: any = {
                    roomIds: newRoomIds,
                    updatedAt: Date.now(),
                };

                // Also update primary roomId if provided
                if (staffData.primaryRoomId) {
                    userUpdates.roomId = staffData.primaryRoomId;
                }

                batch.update(userRef, userUpdates);
                hasUpdates = true;
            } else {
                console.log(`ℹNo room changes detected for staff ${staffId}`);
            }

            // Commit all updates if there are changes
            if (hasUpdates) {
                try {
                    await batch.commit();
                    // Small delay to ensure Firestore consistency
                    await new Promise((resolve) => setTimeout(resolve, 100));
                } catch (commitError) {
                    throw commitError;
                }
            } else {
                console.log(`ℹ No updates needed for staff ${staffId}`);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get unread counts for multiple chats in a single batch operation
     * @param chatIds Array of chat IDs to get unread counts for
     * @param userId The user ID to get unread counts for
     * @returns Observable of unread counts map
     */
    getUnreadCountsBatch(
        chatIds: string[],
        userId: string,
    ): Observable<{ [chatId: string]: number }> {
        if (!chatIds || chatIds.length === 0) {
            return of({});
        }

        const currentUser = this.webStorage.getUserData();

        // For staff users, get unread counts from userChats collection
        if (userId.startsWith('staff_')) {
            const batchPromises = chatIds.map((chatId) =>
                this.firestore
                    .collection('userChats')
                    .doc(userId)
                    .collection('chats')
                    .doc(chatId)
                    .get()
                    .toPromise()
                    .then((doc) => {
                        const data = doc?.data() as any;
                        return {
                            chatId,
                            unreadCount: data?.unreadCount || 0,
                        };
                    })
                    .catch(() => ({ chatId, unreadCount: 0 })),
            );

            return from(Promise.all(batchPromises)).pipe(
                map((results) => {
                    const counts: { [chatId: string]: number } = {};
                    results.forEach((result) => {
                        counts[result.chatId] = result.unreadCount;
                    });
                    return counts;
                }),
                catchError(() => of({})),
            );
        }

        // For parent users, calculate unread counts based on message filtering
        else if (userId.startsWith('parent_')) {
            const parentId = userId.replace('parent_', '');

            const batchPromises = chatIds.map((chatId) =>
                this.firestore
                    .doc<Chat>(`chats/${chatId}`)
                    .get()
                    .toPromise()
                    .then((snapshot) => {
                        const chat = snapshot?.data() as Chat;
                        const messages = chat?.messages || [];
                        const unreadCount = messages.filter((message: any) => {
                            // Don't count messages sent by the current parent
                            if (message.senderId === userId) return false;

                            // For non-private messages, count if not read by this parent
                            if (!message.isPrivate) {
                                return !message.readBy?.[userId];
                            }

                            // For private messages from staff
                            if (message.senderId.startsWith('staff_')) {
                                return (
                                    message.targetGuardianId === parentId &&
                                    !message.readBy?.[userId]
                                );
                            }

                            // For private messages from other parents
                            if (message.senderId.startsWith('parent_')) {
                                return (
                                    message.targetGuardianId === parentId &&
                                    !message.readBy?.[userId]
                                );
                            }

                            return false;
                        }).length;

                        return { chatId, unreadCount };
                    })
                    .catch(() => ({ chatId, unreadCount: 0 })),
            );

            return from(Promise.all(batchPromises)).pipe(
                map((results) => {
                    const counts: { [chatId: string]: number } = {};
                    results.forEach((result) => {
                        counts[result.chatId] = result.unreadCount;
                    });
                    return counts;
                }),
                catchError(() => of({})),
            );
        }

        return of({});
    }

    /**
     * Get user chat summaries with progressive loading (loads in batches)
     * @param userId The user ID
     * @param batchSize Number of chats to load per batch (default: 5)
     * @param startAfter Optional cursor for pagination
     * @returns Observable of chat summary batch with pagination info
     */
    getUserChatSummariesProgressive(
        userId: string,
        batchSize: number = 500,
        startAfter?: any,
    ): Observable<{
        summaries: ChatSummary[];
        hasMore: boolean;
        lastDoc: any;
    }> {
        return this.firestore
            .collection('userChats')
            .doc(userId)
            .collection<UserChat>('chats', (ref) => {
                let query = ref
                    .orderBy('lastVisibleMessage.timestamp', 'desc')
                    .limit(batchSize + 1);

                if (startAfter) {
                    query = query.startAfter(startAfter);
                }

                return query;
            })
            .get()
            .pipe(
                switchMap((snapshot) => {
                    const docs = snapshot.docs;

                    const hasMore = docs.length > batchSize;
                    const slicedDocs = hasMore
                        ? docs.slice(0, batchSize)
                        : docs;
                    const lastDoc = slicedDocs[slicedDocs.length - 1] ?? null;

                    const summaries: ChatSummary[] = slicedDocs.map((doc) => {
                        const data = doc.data() as UserChat;

                        return {
                            chatId: doc.id,
                            lastMessage: data.lastMessage,
                            lastVisibleMessage: data.lastVisibleMessage,
                            unreadCount: data.unreadCount,
                            studentInfo: data.studentInfo,
                            staffInfo: data.staffInfo,
                            otherUser: {
                                id: data.studentInfo?.id || '',
                                name: data.studentInfo?.name || 'Unknown User',
                                role: userId.startsWith('staff_')
                                    ? 'student'
                                    : 'staff',
                                photoUrl: 'assets/layout/images/male.png',
                                roomId: 0,
                                roomIds: [],
                            },
                            isPrivateMode: data.isPrivateMode,
                        };
                    });

                    if (summaries.length === 0) {
                        return of({ summaries, hasMore, lastDoc });
                    }

                    // 🔹 Validate that chats still exist in main collection to filter out orphaned entries
                    const chatIds = summaries.map((s) => s.chatId);
                    const validationPromises = chatIds.map((chatId) =>
                        this.firestore
                            .doc(`chats/${chatId}`)
                            .get()
                            .toPromise()
                            .then((doc) => ({
                                chatId,
                                exists: doc?.exists || false,
                            }))
                            .catch(() => ({ chatId, exists: false })),
                    );

                    return from(Promise.all(validationPromises)).pipe(
                        switchMap((validationResults) => {
                            // Filter out summaries for chats that no longer exist
                            const validSummaries = summaries.filter(
                                (summary) =>
                                    validationResults.find(
                                        (v) => v.chatId === summary.chatId,
                                    )?.exists,
                            );

                            if (validSummaries.length === 0) {
                                return of({ summaries: [], hasMore, lastDoc });
                            }

                            // 🔹 Batch photo fetch for valid summaries only
                            const photoObservables = validSummaries.map(
                                (chatSummary) => {
                                    const studentId = chatSummary.otherUser.id;
                                    if (!studentId || studentId === '') {
                                        return of(
                                            'assets/layout/images/male.png',
                                        );
                                    }

                                    return this.firestore
                                        .doc(`users/${studentId}`)
                                        .valueChanges()
                                        .pipe(
                                            take(1),
                                            map(
                                                (user: any) =>
                                                    user?.profile?.photoURL ||
                                                    'assets/layout/images/male.png',
                                            ),
                                            catchError(() =>
                                                of(
                                                    'assets/layout/images/male.png',
                                                ),
                                            ),
                                        );
                                },
                            );

                            return forkJoin(photoObservables).pipe(
                                map((photoUrls) => ({
                                    summaries: validSummaries.map((s, i) => ({
                                        ...s,
                                        otherUser: {
                                            ...s.otherUser,
                                            photoUrl: photoUrls[i],
                                        },
                                    })),
                                    hasMore,
                                    lastDoc,
                                })),
                            );
                        }),
                    );
                }),
                catchError((error) => {
                    console.error('Error loading chat summaries:', error);
                    return of({ summaries: [], hasMore: false, lastDoc: null });
                }),
            );
    }

    /**
     * Set up global real-time listeners for all chats the user participates in
     * This ensures sidebar updates for messages in non-selected chats
     * @param userId The user ID to set up listeners for
     */
    setupGlobalChatListeners(userId: string): void {
        // Clean up existing listeners
        this.cleanupGlobalChatListeners();

        // Get all chats for this user
        this.firestore
            .collection('userChats')
            .doc(userId)
            .collection('chats')
            .get()
            .toPromise()
            .then((snapshot) => {
                if (!snapshot) return;

                snapshot.docs.forEach((doc) => {
                    const chatId = doc.id;
                    this.setupListenerForChat(chatId, userId);
                });
            })
            .catch((error) => {
                console.error('Error setting up global chat listeners:', error);
            });
    }

    /**
     * Set up real-time listener for a specific chat
     * @param chatId The chat ID to listen to
     * @param userId The user ID
     */
    private setupListenerForChat(chatId: string, userId: string): void {
        // Skip if already listening
        if (this.globalChatListeners[chatId]) {
            return;
        }

        // Listen to chat document changes for real-time updates
        const unsubscribe = this.firestore.firestore
            .doc(`chats/${chatId}`)
            .onSnapshot((snapshot: any) => {
                if (!snapshot.exists) return;

                const chatData = snapshot.data() as Chat;
                if (!chatData || !chatData.messages) return;

                // Get the latest message
                const messages = chatData.messages.sort(
                    (a, b) => b.timestamp - a.timestamp,
                );
                const latestMessage = messages[0];

                if (latestMessage) {
                    // Only emit update if message is not from current user
                    if (latestMessage.senderId !== userId) {
                        this.emitChatUpdate(chatId, {
                            text: latestMessage.text,
                            timestamp: latestMessage.timestamp,
                            senderId: latestMessage.senderId,
                            isDeleted: latestMessage.isDeleted,
                        });
                    }
                }
            });

        this.globalChatListeners[chatId] = unsubscribe;
    }

    /**
     * Clean up all global chat listeners
     */
    cleanupGlobalChatListeners(): void {
        Object.values(this.globalChatListeners).forEach((unsubscribe) => {
            unsubscribe();
        });
        this.globalChatListeners = {};
    }
}
